import React from 'react';
import { Link } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  ChevronLeft, 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  ArrowRight, 
  CheckCircle,
  Clock,
  Share2,
  BookOpen,
  FileText,
  Book,
  Dna,
  BrainCircuit,
  Scale,
  Pill,
  Heart,
  Beaker,
  Activity,
  Flame, // Changed from Flare to Flame
  Shield,
  Target,
  Zap,
  RefreshCw,
  TimerReset,
  PlusCircle,
  MinusCircle,
  Check,
  X,
  Fish,
  Download,
  Sprout
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from '@/components/ui/badge';
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Evidence Box component
const EvidenceBox = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 my-6">
      <div className="flex items-center mb-2">
        <Info className="text-gray-600 h-5 w-5 mr-2" />
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-gray-700 text-sm">
        {children}
      </div>
    </div>
  );
};

// Inflammation Pathway Diagram
const InflammationPathwayDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
      <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
      <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
      </marker>
    </defs>
    
    {/* Background */}
    <rect width="800" height="500" fill="url(#bgGradient)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontFamily="Arial" fontSize="20" fontWeight="bold" fill="#1e293b">Inflammation and Resolution Pathways</text>
    
    {/* Initiating Factors */}
    <circle cx="400" cy="100" r="40" fill="#fee2e2" stroke="#b91c1c" strokeWidth="2" filter="url(#shadow)" />
    <text x="400" y="95" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#7f1d1d">Initiating</text>
    <text x="400" y="115" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#7f1d1d">Stimuli</text>
    
    {/* First Level Nodes */}
    <circle cx="200" cy="200" r="30" fill="#fecaca" stroke="#dc2626" strokeWidth="2" filter="url(#shadow)" />
    <text x="200" y="205" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#7f1d1d">NF-κB</text>
    
    <circle cx="400" cy="200" r="30" fill="#fecaca" stroke="#dc2626" strokeWidth="2" filter="url(#shadow)" />
    <text x="400" y="205" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#7f1d1d">STAT3</text>
    
    <circle cx="600" cy="200" r="30" fill="#fecaca" stroke="#dc2626" strokeWidth="2" filter="url(#shadow)" />
    <text x="600" y="195" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#7f1d1d">COX-2/</text>
    <text x="600" y="210" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#7f1d1d">5-LOX</text>
    
    {/* Connection Lines */}
    <line x1="370" y1="125" x2="230" y2="175" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="400" y1="140" x2="400" y2="165" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="430" y1="125" x2="570" y2="175" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    {/* Second Level: Pro-inflammatory Mediators */}
    <rect x="120" y="280" width="100" height="50" rx="10" ry="10" fill="#fef3c7" stroke="#d97706" strokeWidth="2" filter="url(#shadow)" />
    <text x="170" y="295" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#78350f">IL-6</text>
    <text x="170" y="315" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#78350f">TNF-α</text>
    
    <rect x="360" y="280" width="100" height="50" rx="10" ry="10" fill="#fef3c7" stroke="#d97706" strokeWidth="2" filter="url(#shadow)" />
    <text x="410" y="295" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#78350f">IL-1β</text>
    <text x="410" y="315" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#78350f">IL-8</text>
    
    <rect x="580" y="280" width="100" height="50" rx="10" ry="10" fill="#fef3c7" stroke="#d97706" strokeWidth="2" filter="url(#shadow)" />
    <text x="630" y="295" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#78350f">PGs/LTs</text>
    <text x="630" y="315" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#78350f">Eicosanoids</text>
    
    {/* Connections to Second Level */}
    <line x1="200" y1="230" x2="180" y2="275" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="400" y1="230" x2="400" y2="275" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    <line x1="600" y1="230" x2="620" y2="275" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead)" />
    
    {/* Third Level: Resolution Mediators */}
    <rect x="240" y="380" width="320" height="50" rx="10" ry="10" fill="#d1fae5" stroke="#059669" strokeWidth="2" filter="url(#shadow)" />
    <text x="400" y="405" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#065f46">Specialized Pro-resolving Mediators (SPMs)</text>
    <text x="400" y="420" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#065f46">Lipoxins | Resolvins | Protectins | Maresins</text>
    
    {/* Curved Arrow for Resolution */}
    <path d="M630,330 C650,370 600,410 500,410" fill="none" stroke="#064e3b" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#arrowhead)" />
    <text x="650" y="370" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#064e3b">Active</text>
    <text x="650" y="385" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#064e3b">Resolution</text>
    
    {/* Legend */}
    <circle cx="700" cy="460" r="8" fill="#fecaca" stroke="#dc2626" strokeWidth="1" />
    <text x="715" y="463" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">Pro-inflammatory</text>
    
    <rect x="700" y="480" width="16" height="8" rx="4" ry="4" fill="#d1fae5" stroke="#059669" strokeWidth="1" />
    <text x="725" y="485" textAnchor="start" fontFamily="Arial" fontSize="10" fill="#1e293b">Pro-resolving</text>
  </svg>
);

// Macrophage Polarization Diagram
const MacrophagePolarizationDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="m1Gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#fee2e2" />
        <stop offset="100%" stopColor="#fecaca" />
      </linearGradient>
      <linearGradient id="m2Gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#dcfce7" />
        <stop offset="100%" stopColor="#bbf7d0" />
      </linearGradient>
      <marker id="markerArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
      </marker>
    </defs>
    
    {/* Background */}
    <rect width="800" height="300" fill="#f8fafc" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="30" textAnchor="middle" fontFamily="Arial" fontSize="18" fontWeight="bold" fill="#1e293b">Macrophage Polarization in Inflammation & Resolution</text>
    
    {/* M1 */}
    <circle cx="250" cy="150" r="80" fill="url(#m1Gradient)" stroke="#b91c1c" strokeWidth="2" />
    <text x="250" y="120" textAnchor="middle" fontFamily="Arial" fontSize="16" fontWeight="bold" fill="#7f1d1d">M1 Phenotype</text>
    <text x="250" y="145" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#7f1d1d">(Pro-inflammatory)</text>
    <text x="250" y="165" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#7f1d1d">TNF-α, IL-1β, IL-6, ROS</text>
    <text x="250" y="185" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#7f1d1d">Glycolytic metabolism</text>
    
    {/* M2 */}
    <circle cx="550" cy="150" r="80" fill="url(#m2Gradient)" stroke="#16a34a" strokeWidth="2" />
    <text x="550" y="120" textAnchor="middle" fontFamily="Arial" fontSize="16" fontWeight="bold" fill="#166534">M2 Phenotype</text>
    <text x="550" y="145" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#166534">(Anti-inflammatory)</text>
    <text x="550" y="165" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#166534">IL-10, TGF-β, Arginase-1</text>
    <text x="550" y="185" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#166534">Oxidative phosphorylation</text>
    
    {/* Arrows */}
    <path d="M330,135 C400,105 400,105 470,135" fill="none" stroke="#64748b" strokeWidth="2" markerEnd="url(#markerArrow)" />
    <text x="400" y="110" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#475569">IL-4, IL-13, SPMs</text>
    
    <path d="M470,165 C400,195 400,195 330,165" fill="none" stroke="#64748b" strokeWidth="2" markerEnd="url(#markerArrow)" />
    <text x="400" y="210" textAnchor="middle" fontFamily="Arial" fontSize="11" fill="#475569">LPS, IFN-γ</text>
    
    {/* Legend */}
    <text x="400" y="260" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#475569">Macrophage plasticity is essential for both initiation and resolution of inflammation</text>
    <text x="400" y="275" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#475569">Imbalanced polarization contributes to chronic inflammatory diseases</text>
  </svg>
);

// Cellular Senescence Diagram
const CellularSenescenceDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="cellGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fef3c7" />
        <stop offset="100%" stopColor="#fcd34d" />
      </linearGradient>
      <linearGradient id="saspGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#fee2e2" />
        <stop offset="100%" stopColor="#fca5a5" />
      </linearGradient>
      <linearGradient id="healthyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#d1fae5" />
        <stop offset="100%" stopColor="#6ee7b7" />
      </linearGradient>
      <filter id="cellShadow" x="-10%" y="-10%" width="120%" height="120%">
        <feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.1"/>
      </filter>
      <marker id="saspArrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#dc2626" />
      </marker>
    </defs>
    
    {/* Background */}
    <rect width="800" height="300" fill="#f8fafc" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="30" textAnchor="middle" fontFamily="Arial" fontSize="18" fontWeight="bold" fill="#1e293b">Cellular Senescence and the SASP</text>
    
    {/* Healthy Cell */}
    <circle cx="150" cy="120" r="40" fill="url(#healthyGradient)" stroke="#059669" strokeWidth="2" filter="url(#cellShadow)" />
    <text x="150" y="125" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#065f46">Healthy Cell</text>
    
    {/* Senescent Cell */}
    <circle cx="400" cy="150" r="60" fill="url(#cellGradient)" stroke="#d97706" strokeWidth="2" filter="url(#cellShadow)" />
    <text x="400" y="130" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#92400e">Senescent Cell</text>
    <text x="400" y="150" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#92400e">Cell cycle arrest</text>
    <text x="400" y="165" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#92400e">Resistant to apoptosis</text>
    <text x="400" y="180" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#92400e">Metabolic reprogramming</text>
    
    {/* SASP Factors */}
    <rect x="600" y="80" width="120" height="140" rx="10" ry="10" fill="url(#saspGradient)" stroke="#b91c1c" strokeWidth="2" filter="url(#cellShadow)" />
    <text x="660" y="100" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#7f1d1d">SASP</text>
    <text x="660" y="120" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">Pro-inflammatory</text>
    <text x="660" y="135" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">cytokines</text>
    <text x="660" y="155" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">Chemokines</text>
    <text x="660" y="175" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">Growth factors</text>
    <text x="660" y="195" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">Proteases</text>
    
    {/* Arrows and lines */}
    <line x1="190" y1="120" x2="330" y2="140" stroke="#64748b" strokeWidth="2" />
    <text x="270" y="110" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#475569">Stressors</text>
    <text x="270" y="125" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#475569">(DNA damage, telomere</text>
    <text x="270" y="140" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#475569">shortening, oxidative stress)</text>
    
    <path d="M460,150 C520,150 530,150 590,150" fill="none" stroke="#dc2626" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#saspArrow)" />
    
    {/* Effects */}
    <text x="400" y="240" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e293b">Consequences of SASP Accumulation</text>
    <text x="200" y="260" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">Chronic Inflammation</text>
    <text x="400" y="260" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">Tissue Dysfunction</text>
    <text x="600" y="260" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#7f1d1d">Accelerated Aging</text>
    
    <line x1="660" y1="220" x2="600" y2="245" stroke="#dc2626" strokeWidth="1" />
    <line x1="660" y1="220" x2="400" y2="245" stroke="#dc2626" strokeWidth="1" />
    <line x1="660" y1="220" x2="200" y2="245" stroke="#dc2626" strokeWidth="1" />
  </svg>
);

// Resolution Pathway Diagram
const ResolutionPathwayDiagram = () => (
  <svg width="100%" height="100%" viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="bgGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f8fafc" />
        <stop offset="100%" stopColor="#f1f5f9" />
      </linearGradient>
      <filter id="shadow2" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="2" dy="2" stdDeviation="3" floodOpacity="0.1"/>
      </filter>
      <marker id="arrowhead2" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
        <polygon points="0 0, 10 3.5, 0 7" fill="#64748b" />
      </marker>
    </defs>
    
    {/* Background */}
    <rect width="800" height="500" fill="url(#bgGradient2)" rx="5" ry="5" />
    
    {/* Title */}
    <text x="400" y="40" textAnchor="middle" fontFamily="Arial" fontSize="20" fontWeight="bold" fill="#1e293b">Specialized Pro-resolving Mediators (SPMs)</text>
    
    {/* Fatty Acid Sources */}
    <rect x="100" y="100" width="140" height="60" rx="10" ry="10" fill="#bfdbfe" stroke="#2563eb" strokeWidth="2" filter="url(#shadow2)" />
    <text x="170" y="120" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#1e40af">Omega-3 FAs</text>
    <text x="170" y="140" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#1e40af">EPA, DHA, DPA</text>
    
    <rect x="560" y="100" width="140" height="60" rx="10" ry="10" fill="#bfdbfe" stroke="#2563eb" strokeWidth="2" filter="url(#shadow2)" />
    <text x="630" y="120" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#1e40af">Omega-6 FAs</text>
    <text x="630" y="140" textAnchor="middle" fontFamily="Arial" fontSize="12" fill="#1e40af">Arachidonic Acid</text>
    
    {/* SPMs Nodes */}
    <circle cx="110" cy="250" r="40" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="110" y="245" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Resolvins</text>
    <text x="110" y="265" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#166534">From EPA/DHA</text>
    
    <circle cx="290" cy="250" r="40" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="290" y="245" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Protectins</text>
    <text x="290" y="265" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#166534">From DHA</text>
    
    <circle cx="500" cy="250" r="40" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="500" y="245" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Maresins</text>
    <text x="500" y="265" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#166534">From DHA</text>
    
    <circle cx="680" cy="250" r="40" fill="#bbf7d0" stroke="#16a34a" strokeWidth="2" filter="url(#shadow2)" />
    <text x="680" y="245" textAnchor="middle" fontFamily="Arial" fontSize="14" fontWeight="bold" fill="#166534">Lipoxins</text>
    <text x="680" y="265" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#166534">From AA</text>
    
    {/* Connection Lines */}
    <line x1="160" y1="160" x2="125" y2="210" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="170" y1="160" x2="255" y2="220" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="180" y1="160" x2="460" y2="240" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="630" y1="160" x2="670" y2="210" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Resolution Actions */}
    <rect x="120" y="350" width="130" height="50" rx="10" ry="10" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" filter="url(#shadow2)" />
    <text x="185" y="365" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">↓ Neutrophil</text>
    <text x="185" y="380" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">Infiltration</text>
    
    <rect x="280" y="350" width="130" height="50" rx="10" ry="10" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" filter="url(#shadow2)" />
    <text x="345" y="365" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">↑ Efferocytosis</text>
    <text x="345" y="380" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">(Debris Clearance)</text>
    
    <rect x="440" y="350" width="130" height="50" rx="10" ry="10" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" filter="url(#shadow2)" />
    <text x="505" y="365" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">↑ Tissue Repair</text>
    <text x="505" y="380" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">& Regeneration</text>
    
    <rect x="600" y="350" width="130" height="50" rx="10" ry="10" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" filter="url(#shadow2)" />
    <text x="665" y="365" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">↓ Pain &</text>
    <text x="665" y="380" textAnchor="middle" fontFamily="Arial" fontSize="12" fontWeight="bold" fill="#1e40af">Inflammation</text>
    
    {/* Connections to Resolution Actions */}
    <line x1="120" y1="290" x2="160" y2="345" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="290" y1="290" x2="315" y2="345" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="500" y1="290" x2="505" y2="345" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    <line x1="680" y1="290" x2="665" y2="345" stroke="#64748b" strokeWidth="2" markerEnd="url(#arrowhead2)" />
    
    {/* Legend */}
    <rect x="350" y="430" width="100" height="50" rx="5" ry="5" fill="#f0fdfa" stroke="#0d9488" strokeWidth="1" />
    <text x="400" y="450" textAnchor="middle" fontFamily="Arial" fontSize="10" fontWeight="bold" fill="#134e4a">SPM Actions:</text>
    <text x="400" y="470" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#134e4a">Active Resolution,</text>
    <text x="400" y="480" textAnchor="middle" fontFamily="Arial" fontSize="10" fill="#134e4a">Not Passive Suppression</text>
  </svg>
);

// Create lecture slides content
// Organized into logical sections:
// 1. Introduction & Basic Concepts (slides 1-4)
// 2. Core Inflammatory Mechanisms (slides 5-7) 
// 3. Cross-Talk & System Integration (slides 8-14)
// 4. Assessment & Biomarkers (slide 15)
// 5. Therapeutic Approaches (slides 16-19)
// 6. Conclusion (slide 20)
const lectureSlides: SlideContent[] = [
  // SECTION 1: INTRODUCTION & BASIC CONCEPTS
  // Slide 1: Introduction
  {
    title: "Inflammation, Inflammaging, and Longevity",
    content: (
      <>
        <p className="mb-4">
          Inflammation is a fundamental biological response, essential for survival. It represents the body's coordinated effort to eliminate harmful stimuli, clear debris, and initiate tissue repair. While acute inflammation is protective, chronic inflammation contributes to aging and disease through a process known as "inflammaging."
        </p>
        
        <KeyTakeaway>
          Understanding inflammation as a dynamic, two-phase process—initiation followed by active resolution—reshapes our therapeutic approach. Rather than simply suppressing inflammation, we should aim to promote its natural resolution, addressing the root cause of many age-related diseases.
        </KeyTakeaway>
        
        <p className="mt-4">
          This lecture explores the mechanisms, biomarkers, and clinical strategies for managing inflammation to optimize health and potentially extend healthspan.
        </p>
      </>
    ),
  },
  
  // Slide 2: The Inflammatory Response Defined
  {
    title: "The Inflammatory Response: A Double-Edged Sword",
    content: (
      <>
        <p className="mb-4">
          Inflammation occurs in two phases: <strong>initiation</strong> and <strong>resolution</strong>. Both are active processes requiring precise coordination of molecular and cellular events. Dysregulation at either stage can lead to chronic inflammation.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2 flex items-center">
              <PlusCircle className="h-5 w-5 mr-2 text-red-500" />
              Acute Inflammation (Beneficial)
            </h3>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              <li>Pathogen elimination</li>
              <li>Tissue repair initiation</li>
              <li>Adaptive immunity activation</li>
              <li>Self-limiting with active resolution</li>
            </ul>
          </div>
          
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h3 className="font-semibold text-amber-800 mb-2 flex items-center">
              <MinusCircle className="h-5 w-5 mr-2 text-amber-500" />
              Chronic Inflammation (Harmful)
            </h3>
            <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
              <li>Persistent tissue damage</li>
              <li>Metabolic dysfunction</li>
              <li>Cellular senescence promotion</li>
              <li>Failure of resolution processes</li>
            </ul>
          </div>
        </div>
        
        <ClinicalNote>
          Age-related chronic inflammation ("inflammaging") is characterized by persistent low-grade activation of innate immunity and pro-inflammatory signaling pathways, without effective resolution—creating a fertile environment for age-related diseases.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 3: Key Inflammatory Mediators
  {
    title: "Key Inflammatory Mediators and Pathways",
    content: (
      <>
        <p className="mb-4">
          Inflammation is coordinated by a complex network of signaling molecules and transcription factors. Understanding these pathways provides targets for intervention.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Primary Pro-inflammatory Cytokines</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="font-medium text-red-600 mr-2">IL-6:</span>
                <span className="text-sm">Pleiotropic cytokine crucial for acute phase responses and metabolic control. Chronically elevated IL-6 is implicated in disease pathogenesis.</span>
              </li>
              <li className="flex items-start">
                <span className="font-medium text-red-600 mr-2">TNF-α:</span>
                <span className="text-sm">Potent pro-inflammatory cytokine that drives inflammation, recruits immune cells, and contributes to tissue damage when chronically elevated.</span>
              </li>
              <li className="flex items-start">
                <span className="font-medium text-red-600 mr-2">IL-1β:</span>
                <span className="text-sm">Primary mediator produced via inflammasome activation that induces fever, activates immune cells, and stimulates other inflammatory mediators.</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Key Transcription Factors</h3>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="font-medium text-red-600 mr-2">NF-κB:</span>
                <span className="text-sm">Master regulator activated by diverse stimuli that induces transcription of numerous pro-inflammatory genes. Chronic activation is a hallmark of inflammatory diseases.</span>
              </li>
              <li className="flex items-start">
                <span className="font-medium text-red-600 mr-2">STAT3:</span>
                <span className="text-sm">Activated primarily by cytokines like IL-6, promoting expression of genes involved in inflammation, cell survival, and proliferation.</span>
              </li>
              <li className="flex items-start">
                <span className="font-medium text-red-600 mr-2">IL-6 Amplifier:</span>
                <span className="text-sm">A positive feedback loop between NF-κB and STAT3 that can perpetuate chronic inflammation in non-immune cells like fibroblasts and adipocytes.</span>
              </li>
            </ul>
          </div>
        </div>
        
        <InflammationPathwayDiagram />
      </>
    ),
  },
  
  // Slide 4: Specialized Pro-resolving Mediators
  {
    title: "Specialized Pro-resolving Mediators (SPMs)",
    content: (
      <>
        <p className="mb-4">
          Resolution of inflammation is an active process mediated by specialized lipid-derived mediators collectively known as SPMs. These are derived from omega-3 and omega-6 polyunsaturated fatty acids through enzymatic pathways.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2">Major Classes of SPMs</h3>
            <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
              <li><strong>Lipoxins (LXs):</strong> Derived from Arachidonic Acid (AA)</li>
              <li><strong>Resolvins (Rvs):</strong> E-series from EPA, D-series from DHA</li>
              <li><strong>Protectins (PDs):</strong> Derived from DHA</li>
              <li><strong>Maresins (MaRs):</strong> Derived from DHA via 12-LOX</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">The Five Pillars of Resolution</h3>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
              <li>Stopping neutrophil infiltration</li>
              <li>Enhancing efferocytosis (clearance of apoptotic cells)</li>
              <li>Switching mediator production from pro- to anti-inflammatory</li>
              <li>Promoting tissue repair and regeneration</li>
              <li>Reducing pain through direct analgesic effects</li>
            </ul>
          </div>
        </div>
        
        <ResolutionPathwayDiagram />
        
        <ClinicalNote>
          Unlike traditional anti-inflammatory agents that suppress the immune response, SPMs represent 'resolution agonists' that enhance endogenous repair mechanisms. NSAIDs and corticosteroids can sometimes inhibit SPM production, potentially interfering with resolution.
        </ClinicalNote>
      </>
    ),
  },
  
  // SECTION 2: CORE INFLAMMATORY MECHANISMS
  // Slide 5: Macrophage Polarization
  {
    title: "Macrophage Polarization: Key Players in Inflammation & Resolution",
    content: (
      <>
        <p className="mb-4">
          Macrophages are central to both the initiation and resolution of inflammation. Their remarkable plasticity allows them to change their phenotype (polarize) in response to environmental cues, adopting either pro-inflammatory (M1) or anti-inflammatory/pro-resolving (M2) states.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2 flex items-center">
              <Flame className="h-5 w-5 mr-2 text-red-500" />
              M1 (Pro-inflammatory)
            </h3>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              <li><strong>Activating Signals:</strong> LPS, IFN-γ, GM-CSF</li>
              <li><strong>Transcription Factors:</strong> NF-κB, STAT1, IRF5</li>
              <li><strong>Metabolic Profile:</strong> Glycolytic (Warburg-like)</li>
              <li><strong>Products:</strong> TNF-α, IL-1β, IL-6, IL-12, IL-23, ROS</li>
              <li><strong>Function:</strong> Pathogen killing, tumor resistance</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center">
              <Sprout className="h-5 w-5 mr-2 text-green-500" />
              M2 (Anti-inflammatory)
            </h3>
            <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
              <li><strong>Activating Signals:</strong> IL-4, IL-13, IL-10, M-CSF</li>
              <li><strong>Transcription Factors:</strong> STAT6, PPARγ, KLF4</li>
              <li><strong>Metabolic Profile:</strong> Oxidative phosphorylation</li>
              <li><strong>Products:</strong> IL-10, TGF-β, Arginase-1, SPMs</li>
              <li><strong>Function:</strong> Tissue repair, helminth clearance</li>
            </ul>
          </div>
        </div>
        
        <ClinicalNote>
          The M1/M2 paradigm is an oversimplification of a spectrum of macrophage states. In vivo, macrophages often display mixed phenotypes based on their tissue microenvironment. Chronic inflammation is characterized by a persistence of M1-like macrophages and/or impaired transition to M2-like states. Targeting macrophage polarization represents a promising therapeutic strategy.
        </ClinicalNote>
        
        <MacrophagePolarizationDiagram />
      </>
    ),
  },
  
  // Slide 6: Inflammaging and Longevity
  {
    title: "Inflammaging and Longevity",
    content: (
      <>
        <p className="mb-4">
          "Inflammaging" refers to the chronic, low-grade, sterile inflammation that develops with advancing age. It is characterized by elevated levels of pro-inflammatory markers in the absence of overt infection, representing a major contributor to age-related diseases and functional decline.
        </p>
        
        <div className="bg-violet-50 p-5 rounded-lg border border-violet-200 mb-6">
          <h3 className="font-semibold text-violet-800 mb-2">Sources of Inflammaging</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-violet-700">
            <div className="flex items-start">
              <Dna className="h-5 w-5 mr-2 text-violet-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Genomic Instability</p>
                <p>DNA damage accumulation activates inflammatory pathways</p>
              </div>
            </div>
            <div className="flex items-start">
              <Target className="h-5 w-5 mr-2 text-violet-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Cellular Senescence</p>
                <p>SASP (Senescence-Associated Secretory Phenotype) includes pro-inflammatory factors</p>
              </div>
            </div>
            <div className="flex items-start">
              <Zap className="h-5 w-5 mr-2 text-violet-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Mitochondrial Dysfunction</p>
                <p>Mitochondrial ROS and DAMPs trigger inflammatory responses</p>
              </div>
            </div>
            <div className="flex items-start">
              <RefreshCw className="h-5 w-5 mr-2 text-violet-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Dysbiosis</p>
                <p>Age-related changes in gut microbiota composition and intestinal permeability</p>
              </div>
            </div>
          </div>
        </div>
        
        <KeyTakeaway>
          Inflammaging represents a critical target in longevity medicine. Interventions that reduce chronic inflammation without compromising immune function may significantly extend healthspan. The most effective approaches likely combine reduction of pro-inflammatory triggers with enhancement of resolution pathways.
        </KeyTakeaway>
      </>
    ),
  },
  
  // Slide 7: Cellular Senescence and Inflammaging
  {
    title: "Cellular Senescence and Inflammaging",
    content: (
      <>
        <p className="mb-4">
          Cellular senescence—a state of permanent cell cycle arrest—is a significant contributor to inflammaging. Senescent cells accumulate with age and secrete a complex mix of inflammatory factors, growth regulators, and proteases known as the Senescence-Associated Secretory Phenotype (SASP).
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">SASP Components</h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Pro-inflammatory cytokines:</strong> IL-6, IL-1α/β, IL-8, TNF-α</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Chemokines:</strong> MCP-1, MIP-1α, CXCL1/2</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Growth factors:</strong> VEGF, HGF, TGF-β</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Proteases:</strong> MMPs, PAI-1, uPA</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
            <h3 className="font-semibold text-orange-800 mb-2">Consequences of Senescent Cell Accumulation</h3>
            <ul className="list-disc list-inside text-sm text-orange-700 space-y-1">
              <li>Chronic, sterile inflammation (inflammaging)</li>
              <li>Tissue dysfunction and disrupted architecture</li>
              <li>Impaired stem cell function</li>
              <li>Accelerated aging of neighboring cells (bystander senescence)</li>
              <li>Exacerbation of age-related diseases</li>
            </ul>
          </div>
        </div>
        
        <CellularSenescenceDiagram />
        
        <KeyTakeaway>
          The elimination of senescent cells (senolysis) has emerged as a promising therapeutic strategy for reducing inflammaging and potentially extending healthspan. Both pharmacological senolytic agents and natural compounds with senolytic activity are being investigated for their ability to selectively remove senescent cells while preserving healthy tissue function.
        </KeyTakeaway>
      </>
    ),
  },
  
  // SECTION 3: CROSS-TALK & SYSTEM INTEGRATION
  // Slide 8: Peroxisomes and Inflammation
  {
    title: "Peroxisomal Function in Inflammation and Resolution",
    content: (
      <>
        <p className="mb-4">
          Peroxisomes are organelles with crucial roles in cellular metabolism and redox balance. Their dysfunction is increasingly recognized as a contributor to chronic inflammation and impaired resolution.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">Key Peroxisomal Functions</h3>
            <ul className="space-y-2 text-sm text-blue-700">
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>β-oxidation of very-long-chain fatty acids (VLCFAs)</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Biosynthesis of ether-phospholipids (plasmalogens)</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Regulation of cellular ROS via catalase</span>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Processing of DHA for SPM synthesis</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2">Consequences of Peroxisomal Dysfunction</h3>
            <ul className="space-y-2 text-sm text-red-700">
              <li className="flex items-start">
                <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span>Accumulation of VLCFAs (pro-inflammatory)</span>
              </li>
              <li className="flex items-start">
                <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span>Reduced plasmalogen synthesis (compromising membrane integrity)</span>
              </li>
              <li className="flex items-start">
                <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span>Increased oxidative stress (amplifying inflammation)</span>
              </li>
              <li className="flex items-start">
                <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span>Decreased DHA availability for SPM production</span>
              </li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          Peroxisomal function declines with age and in various diseases, potentially contributing to inflammaging. Nutritional and pharmacological strategies that enhance peroxisomal function may support resolution pathways and reduce chronic inflammation. Key interventions include DHA/EPA supplementation, plasmalogens or their precursors, and compounds that activate peroxisome proliferator-activated receptors (PPARs).
        </KeyTakeaway>
      </>
    ),
  },
  
  // SECTION 3: CROSS-TALK & SYSTEM INTEGRATION
  // Slide 8: Immune-Metabolic Interactions
  {
    title: "Metabolic Inflammation: Bidirectional Crosstalk",
    content: (
      <>
        <p className="mb-4">
          The interplay between metabolism and inflammation represents a critical axis in both health and disease. Dysregulated metabolism promotes chronic inflammation, while inflammatory processes disrupt metabolic homeostasis.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h3 className="font-semibold text-amber-800 mb-2">Metabolic Triggers of Inflammation</h3>
            <ul className="space-y-2 text-sm text-amber-700">
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Nutrient excess:</strong> Lipotoxicity, glucotoxicity, hyperuricemia</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Adipocyte hypertrophy:</strong> Hypoxia and necrosis-driven inflammation</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Lipid mediators:</strong> Imbalance between pro- and anti-inflammatory eicosanoids</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-amber-500 flex-shrink-0 mt-0.5" />
                <span><strong>Advanced glycation end-products (AGEs):</strong> RAGE pathway activation</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2">Inflammatory Disruption of Metabolism</h3>
            <ul className="space-y-2 text-sm text-red-700">
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span><strong>Insulin resistance:</strong> TNF-α and IL-6 impair insulin signaling</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span><strong>Dyslipidemia:</strong> Pro-inflammatory cytokines alter lipid metabolism</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span><strong>Mitochondrial dysfunction:</strong> Inflammatory mediators disrupt bioenergetics</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-red-500 flex-shrink-0 mt-0.5" />
                <span><strong>Anabolic resistance:</strong> Inflammation impairs muscle protein synthesis</span>
              </li>
            </ul>
          </div>
        </div>
        
        <ClinicalNote>
          Metabolic inflammation represents a key therapeutic target in age-related diseases. Lifestyle interventions that improve metabolic health—such as Mediterranean diet, time-restricted eating, and targeted exercise—have been shown to reduce inflammatory biomarkers and enhance resolution capacity. Pharmaceutical approaches that target specific nodes in metabolic inflammation include AMPK activators (metformin), PPAR agonists (glitazones), and GLP-1 receptor agonists.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 9: Nervous System Crosstalk
  {
    title: "Neuro-immune Interactions",
    content: (
      <>
        <p className="mb-4">
          The nervous system and immune system engage in bidirectional communication that profoundly influences inflammatory processes. These neuro-immune interactions provide important regulatory mechanisms that can either promote or resolve inflammation, with significant implications for longevity medicine.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 mb-2 flex items-center">
              <BrainCircuit className="h-5 w-5 mr-2 text-indigo-600" />
              Cholinergic Anti-inflammatory Pathway (CAP)
            </h3>
            <p className="text-sm text-indigo-700 mb-2">
              A neural circuit that modulates inflammation through vagus nerve stimulation and acetylcholine release
            </p>
            <ul className="space-y-1 text-sm text-indigo-700">
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-indigo-500 flex-shrink-0 mt-0.5" />
                <span>Sensory vagal afferents detect inflammatory signals</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-indigo-500 flex-shrink-0 mt-0.5" />
                <span>Vagal efferents release acetylcholine in the spleen</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-indigo-500 flex-shrink-0 mt-0.5" />
                <span>ACh binds α7nAChR on macrophages</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-indigo-500 flex-shrink-0 mt-0.5" />
                <span>Inhibits NF-κB signaling and pro-inflammatory cytokine production</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              HPA Axis and Stress Response
            </h3>
            <p className="text-sm text-blue-700 mb-2">
              Hypothalamic-pituitary-adrenal axis regulates inflammation through glucocorticoid release
            </p>
            <ul className="space-y-1 text-sm text-blue-700">
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Acute stress: Adaptive anti-inflammatory effects via cortisol</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Chronic stress: Glucocorticoid resistance and prolonged inflammation</span>
              </li>
              <li className="flex items-start">
                <ArrowRight className="h-4 w-4 mr-2 text-blue-500 flex-shrink-0 mt-0.5" />
                <span>Aging: Progressive HPA axis dysregulation contributes to inflammaging</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-6">
          <h3 className="font-semibold text-green-800 mb-2">Clinical Applications of Neuro-immune Modulation</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-green-700">
            <div className="flex items-start">
              <Check className="h-4 w-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Vagus Nerve Stimulation (VNS)</p>
                <p>Implantable or non-invasive devices that activate the CAP, showing promise in inflammatory conditions like rheumatoid arthritis</p>
              </div>
            </div>
            <div className="flex items-start">
              <Check className="h-4 w-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Mind-Body Interventions</p>
                <p>Meditation, yoga, and breathing exercises modulate vagal tone and reduce inflammatory markers</p>
              </div>
            </div>
            <div className="flex items-start">
              <Check className="h-4 w-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">α7nAChR Agonists</p>
                <p>Pharmacological compounds that mimic acetylcholine's anti-inflammatory effects</p>
              </div>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          The neuro-immune interface offers novel therapeutic targets for modulating inflammation in aging populations. Heart rate variability (HRV) serves as a useful non-invasive biomarker of vagal tone and autonomic balance, with lower HRV associated with increased inflammation and accelerated aging. HRV biofeedback training may represent a practical clinical intervention to enhance parasympathetic activity and reduce inflammatory burden.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 10: Nutritional Approaches
  {
    title: "Nutritional Approaches to Resolve Inflammation",
    content: (
      <>
        <p className="mb-4">
          Dietary interventions represent first-line strategies for modulating inflammation and enhancing resolution pathways. Nutrition affects inflammation through multiple mechanisms, including direct impact on inflammatory signaling, microbiome composition, and precursor availability for SPM synthesis.
        </p>
        
        <div className="bg-green-50 p-5 rounded-lg border border-green-200 mb-6">
          <h3 className="font-semibold text-green-800 mb-3">Evidence-Based Anti-Inflammatory Dietary Patterns</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                <span className="text-green-700 font-medium">1</span>
              </div>
              <div>
                <p className="font-medium text-green-800">Mediterranean Diet</p>
                <p className="text-sm text-green-700">High in monounsaturated fats, omega-3s, polyphenols, and fiber. Associated with reduced inflammatory markers (CRP, IL-6) and cardiovascular risk.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                <span className="text-green-700 font-medium">2</span>
              </div>
              <div>
                <p className="font-medium text-green-800">DASH Diet</p>
                <p className="text-sm text-green-700">Emphasizes fruits, vegetables, whole grains, and lean proteins. Reduces inflammatory markers and blood pressure.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                <span className="text-green-700 font-medium">3</span>
              </div>
              <div>
                <p className="font-medium text-green-800">Plant-Based/Polyphenol-Rich</p>
                <p className="text-sm text-green-700">Fiber fermentation produces SCFAs with anti-inflammatory properties. Polyphenols modulate NF-κB signaling.</p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-2 flex-shrink-0 mt-0.5">
                <span className="text-green-700 font-medium">4</span>
              </div>
              <div>
                <p className="font-medium text-green-800">Time-Restricted Feeding</p>
                <p className="text-sm text-green-700">Enhances autophagy and reduces metabolic inflammation. Synergistic with anti-inflammatory diets.</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-3">Key Nutrients for Resolution Promotion</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex flex-col items-center text-center">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Fish className="h-6 w-6 text-blue-600" />
              </div>
              <p className="font-medium text-blue-800">Omega-3 Fatty Acids</p>
              <p className="text-blue-700">EPA/DHA provide direct SPM precursors. Aim for 1-3g combined EPA/DHA daily for anti-inflammatory effects.</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Pill className="h-6 w-6 text-blue-600" />
              </div>
              <p className="font-medium text-blue-800">Polyphenols</p>
              <p className="text-blue-700">Resveratrol, curcumin, EGCG inhibit NF-κB and enhance resolution. Often require enhanced bioavailability formulations.</p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Beaker className="h-6 w-6 text-blue-600" />
              </div>
              <p className="font-medium text-blue-800">SPM Precursors</p>
              <p className="text-blue-700">Specialized supplements providing direct SPM precursors or low-dose SPMs themselves show promise in clinical studies.</p>
            </div>
          </div>
        </div>
      </>
    ),
  },
  
  // Slide 11: Lifestyle and Pharmacological Approaches
  {
    title: "Lifestyle and Pharmacological Approaches",
    content: (
      <>
        <p className="mb-4">
          Beyond nutrition, various lifestyle and pharmacological interventions can modulate inflammatory pathways and promote resolution. An integrative approach typically yields the best results.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-indigo-50 p-5 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-indigo-800 mb-3 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-indigo-600" />
              Lifestyle Interventions
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-indigo-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-indigo-700">Exercise</p>
                  <p className="text-sm text-indigo-600">Regular moderate exercise reduces pro-inflammatory markers and promotes anti-inflammatory myokines. High-intensity training can temporarily increase inflammation but enhances long-term resolution capacity.</p>
                </div>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-indigo-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-indigo-700">Stress Management</p>
                  <p className="text-sm text-indigo-600">Chronic stress promotes inflammation via HPA axis dysregulation. Meditation, yoga, and mindfulness practices reduce inflammatory markers.</p>
                </div>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-indigo-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-indigo-700">Sleep Optimization</p>
                  <p className="text-sm text-indigo-600">Sleep disruption is pro-inflammatory. Aim for 7-8 hours of quality sleep with consistent timing to support resolution processes.</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-5 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-3 flex items-center">
              <Pill className="h-5 w-5 mr-2 text-purple-600" />
              Pharmacological Approaches
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-purple-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-purple-700">Low-Dose Aspirin</p>
                  <p className="text-sm text-purple-600">Triggers production of aspirin-triggered lipoxins (ATLs) and resolvins through acetylated COX-2. 81mg daily may support resolution processes.</p>
                </div>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-purple-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-purple-700">SPM-Based Therapeutics</p>
                  <p className="text-sm text-purple-600">Synthetic or isolated SPMs showing promise in inflammatory conditions. Early clinical trials for conditions like periodontitis and dry eye.</p>
                </div>
              </li>
              <li className="flex items-start">
                <Check className="h-4 w-4 mr-2 text-purple-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-purple-700">PPAR Agonists</p>
                  <p className="text-sm text-purple-600">Activate peroxisome proliferation and enhance fatty acid metabolism. May support SPM production pathways.</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        <ClinicalNote>
          When selecting anti-inflammatory interventions, consider agents that not only suppress inflammation but also promote resolution. NSAIDs may provide short-term symptomatic relief but can potentially impair resolution processes if used chronically, especially at higher doses. For chronic inflammatory conditions, prioritize resolution-enhancing strategies alongside careful, targeted suppression of excessive inflammation.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 12: Clinical Applications and Protocols
  {
    title: "Clinical Applications and Protocols for Practice",
    content: (
      <>
        <p className="mb-4">
          Implementing inflammation-focused strategies in clinical practice requires a structured approach. The following framework provides guidance for assessment, intervention, and monitoring.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
              Assessment
            </h3>
            <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1">
              <li>Comprehensive inflammatory biomarkers (hsCRP, IL-6, TNF-α)</li>
              <li>Oxidative stress markers (8-iso-PGF2α, 8-OHdG)</li>
              <li>Evaluation of resolution capacity (if available)</li>
              <li>Assessment of inflammaging risk factors</li>
              <li>Identification of inflammatory triggers (food sensitivities, environmental exposures)</li>
            </ol>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-green-600" />
              Intervention
            </h3>
            <ol className="list-decimal list-inside text-sm text-green-700 space-y-1">
              <li>Anti-inflammatory diet personalized to patient</li>
              <li>Targeted nutritional supplements:
                <ul className="list-disc list-inside ml-4 mt-1">
                  <li>Omega-3s (1-3g EPA/DHA)</li>
                  <li>Bioavailable curcumin (500-1000mg)</li>
                  <li>SPM precursors/promoters</li>
                </ul>
              </li>
              <li>Lifestyle optimization (exercise, sleep, stress management)</li>
              <li>Targeted pharmacological approaches if indicated</li>
            </ol>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
              <TimerReset className="h-5 w-5 mr-2 text-purple-600" />
              Monitoring and Adaptation
            </h3>
            <ol className="list-decimal list-inside text-sm text-purple-700 space-y-1">
              <li>Reassess inflammatory markers at 3-6 months</li>
              <li>Track clinical symptoms and functional outcomes</li>
              <li>Adjust interventions based on response</li>
              <li>Consider more aggressive approaches for non-responders</li>
              <li>Long-term surveillance for inflammaging progression</li>
            </ol>
          </div>
        </div>
        
        <div className="bg-amber-50 p-5 rounded-lg border border-amber-200">
          <h3 className="font-semibold text-amber-800 mb-3">Clinical Protocols for Common Inflammatory Phenotypes</h3>
          
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-amber-600 font-medium">1</span>
              </div>
              <div>
                <p className="font-medium text-amber-900">Metabolic Inflammation</p>
                <p className="text-sm text-amber-700 mt-1">
                  Characterized by insulin resistance, elevated visceral adiposity, and metabolic endotoxemia. Treatment focuses on time-restricted feeding, high-fiber Mediterranean diet, targeted omega-3s (2-4g EPA/DHA), and improving gut barrier function. Regular moderate exercise is crucial for mobilizing inflammatory visceral fat.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-amber-600 font-medium">2</span>
              </div>
              <div>
                <p className="font-medium text-amber-900">Neuroinflammation</p>
                <p className="text-sm text-amber-700 mt-1">
                  Often presents with cognitive changes, mood alterations, and fatigue. Interventions include DHA-rich omega-3 supplementation (1.5-2g DHA), polyphenol concentrates, elimination of gluten/dairy if sensitive, and resolution-promoting supplements like SPM precursors or low-dose specialized lipid mediators.
                </p>
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mr-3 mt-0.5">
                <span className="text-amber-600 font-medium">3</span>
              </div>
              <div>
                <p className="font-medium text-amber-900">Autoimmune-Driven Inflammation</p>
                <p className="text-sm text-amber-700 mt-1">
                  Requires careful balance between immune modulation and resolution promotion. Consider autoimmune protocol diet, higher-dose EPA/DHA (3-4g), vitamin D optimization, gut microbiome restoration, and in some cases, low-dose naltrexone to modulate immune function while preserving resolution processes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    ),
  },
  
  // Slide 13: Mechanisms - Macrophage Polarization
  {
    title: "Macrophage Polarization: Key Players in Inflammation & Resolution",
    content: (
      <>
        <p className="mb-4">
          Macrophages exist on a functional spectrum and can shift phenotypes in response to environmental signals.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2 flex items-center">
              <Flame className="h-5 w-5 mr-2 text-red-600" />
              M1 (Pro-inflammatory)
            </h3>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1 ml-1">
              <li>Activated by: IFN-γ, LPS (bacterial endotoxin), TNF-α</li>
              <li>Secretes: IL-1β, IL-6, IL-12, IL-23, TNF-α, ROS</li>
              <li>Functions: Pathogen killing, pro-inflammatory</li>
              <li>Metabolism: Glycolytic (Warburg-like)</li>
              <li>Cell surface markers: CD80, CD86, HLA-DR</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center">
              <Shield className="h-5 w-5 mr-2 text-green-600" />
              M2 (Resolution/Repair)
            </h3>
            <ul className="list-disc list-inside text-sm text-green-700 space-y-1 ml-1">
              <li>Activated by: IL-4, IL-13, IL-10, TGF-β, glucocorticoids</li>
              <li>Secretes: IL-10, TGF-β, VEGF, SPMs</li>
              <li>Functions: Resolution, tissue repair, angiogenesis</li>
              <li>Metabolism: Oxidative phosphorylation</li>
              <li>Cell surface markers: CD163, CD206, CD200R</li>
            </ul>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="relative h-20 bg-gradient-to-r from-red-100 via-amber-50 to-green-100 rounded-lg overflow-hidden">
            <div className="absolute inset-0 flex items-center justify-between px-6">
              <div className="text-center">
                <span className="block text-red-600 font-semibold">M1</span>
                <span className="text-xs text-red-500">Pro-inflammatory</span>
              </div>
              
              <div className="text-center">
                <span className="block text-amber-600 font-semibold">M0</span>
                <span className="text-xs text-amber-500">Naive</span>
              </div>
              
              <div className="text-center">
                <span className="block text-green-600 font-semibold">M2</span>
                <span className="text-xs text-green-500">Pro-resolving</span>
              </div>
            </div>
            
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 via-amber-400 to-green-400"></div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-red-400 via-amber-400 to-green-400"></div>
            
            <div className="absolute top-1/2 left-1/4 transform -translate-y-1/2">
              <div className="h-12 w-12 rounded-full bg-white shadow-md flex items-center justify-center">
                <Flame className="h-6 w-6 text-red-500" />
              </div>
            </div>
            
            <div className="absolute top-1/2 right-1/4 transform -translate-y-1/2">
              <div className="h-12 w-12 rounded-full bg-white shadow-md flex items-center justify-center">
                <Shield className="h-6 w-6 text-green-500" />
              </div>
            </div>
            
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="flex items-center space-x-1">
                <ArrowLeft className="h-4 w-4 text-gray-500" />
                <ArrowRight className="h-4 w-4 text-gray-500" />
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-700 mb-6">
          During aging, the balance shifts toward M1 dominance. Senescent cells and age-related factors like oxidized lipids and AGEs promote M1 polarization. Resolution pathways (SPMs, efferocytosis) become less efficient.
        </p>
        
        <ClinicalNote>
          Modulating macrophage polarization (e.g., via SPMs) is a therapeutic target in age-related inflammatory conditions. Exercise and caloric restriction promote M2 polarization and enhance tissue healing and regeneration.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 14: Mechanisms - Kynurenine Pathway
  {
    title: "Metabolic Hijack: Kynurenine Pathway & Inflammation",
    content: (
      <>
        <p className="mb-4">
          The kynurenine pathway represents a critical intersection between inflammation and neurological health through tryptophan metabolism.
        </p>
        
        <div className="bg-gray-50 p-5 rounded-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-3">Tryptophan Metabolism</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-1">
                <li>Tryptophan: Essential amino acid, precursor for Serotonin (5-HT) and Kynurenine Pathway (KP)</li>
                <li>Normal state: Balanced metabolism between serotonin and kynurenine pathways</li>
                <li>Inflammatory state: Pro-inflammatory cytokines (especially IFN-γ) strongly induce IDO/TDO enzymes</li>
              </ul>
            </div>
            
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-3">Consequences</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-1">
                <li>Tryptophan shunted away from 5-HT synthesis → Reduced serotonin availability</li>
                <li>Increased production of neuroactive KP metabolites:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Kynurenic Acid (KA): Generally neuroprotective, antioxidant</li>
                    <li>Quinolinic Acid (QA): Neurotoxic, excitotoxic, pro-oxidant</li>
                  </ul>
                </li>
                <li>Inflammation often skews balance towards QA → Neurotoxicity</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="relative bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col items-center">
              <div className="mb-2 w-full text-center py-2 bg-purple-100 rounded-md">
                <span className="font-semibold text-purple-800">Tryptophan</span>
              </div>
              
              <div className="grid grid-cols-2 gap-8 w-full mb-4">
                <div className="flex flex-col items-center">
                  <div className="h-20 w-0.5 bg-blue-400"></div>
                  <div className="p-2 bg-blue-100 rounded-md text-center mt-2 w-full">
                    <div className="text-sm font-medium text-blue-800">Normal Pathway</div>
                    <div className="text-xs text-blue-600">Tryptophan Hydroxylase</div>
                  </div>
                  <div className="h-16 w-0.5 bg-blue-400"></div>
                  <div className="p-2 bg-blue-100 rounded-md text-center w-full">
                    <div className="text-sm font-medium text-blue-800">Serotonin (5-HT)</div>
                    <div className="text-xs text-blue-600">Mood, Sleep, Cognition</div>
                  </div>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="h-20 w-0.5 bg-red-400"></div>
                  <div className="p-2 bg-red-100 rounded-md text-center mt-2 w-full">
                    <div className="text-sm font-medium text-red-800">Inflammation (IDO/TDO↑)</div>
                    <div className="text-xs text-red-600">IFN-γ, TNF-α, IL-1β</div>
                  </div>
                  <div className="h-16 w-0.5 bg-red-400"></div>
                  <div className="p-2 bg-amber-100 rounded-md text-center w-full">
                    <div className="text-sm font-medium text-amber-800">Kynurenine Pathway</div>
                  </div>
                  
                  <div className="h-8 w-0.5 bg-amber-400"></div>
                  <div className="grid grid-cols-2 gap-4 w-full">
                    <div className="p-2 bg-green-100 rounded-md text-center">
                      <div className="text-xs font-medium text-green-800">KA</div>
                      <div className="text-xs text-green-600">Neuroprotective</div>
                    </div>
                    <div className="p-2 bg-red-100 rounded-md text-center">
                      <div className="text-xs font-medium text-red-800">QA</div>
                      <div className="text-xs text-red-600">Neurotoxic</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-4 right-4 bg-red-100 rounded-full p-2">
              <span className="text-xs font-medium text-red-800">Inflammation skews toward QA</span>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          The kynurenine pathway is linked to inflammation-associated depression, anxiety, fatigue, and cognitive decline. Strategies that modulate IDO activity or the KA/QA ratio may provide therapeutic benefit in inflammatory brain conditions.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 15: Mechanisms - Tyrosine Pathway
  {
    title: "Metabolic Hijack: Tyrosine Pathway & Inflammation",
    content: (
      <>
        <p className="mb-4">
          Inflammation interferes with catecholamine synthesis from tyrosine, affecting mood, motivation, and cognitive function.
        </p>
        
        <div className="bg-gray-50 p-5 rounded-lg mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-3">Tyrosine Metabolism</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-1">
                <li>Tyrosine: Precursor for Dopamine, Norepinephrine, Epinephrine</li>
                <li>Rate-limiting enzyme: Tyrosine Hydroxylase (TH)</li>
                <li>TH requires tetrahydrobiopterin (BH4) as cofactor</li>
                <li>Normal state: Efficient catecholamine synthesis</li>
              </ul>
            </div>
            
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-3">Inflammatory Impact</h3>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-2 ml-1">
                <li>Cytokines increase ROS/RNS → Oxidize and deplete BH4</li>
                <li>Reduced BH4 limits TH activity → Decreased L-DOPA & catecholamine synthesis</li>
                <li>May impair dopamine packaging (VMAT2↓) and increase reuptake (DAT↑)</li>
                <li>Result: Lower dopamine availability despite normal tyrosine levels</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="relative bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex flex-col items-center">
              <div className="mb-2 w-full text-center py-2 bg-amber-100 rounded-md">
                <span className="font-semibold text-amber-800">Tyrosine</span>
              </div>
              
              <div className="flex items-center justify-center w-full mb-4">
                <div className="text-center">
                  <div className="h-16 w-0.5 bg-blue-400 mx-auto"></div>
                  <div className="p-2 bg-blue-100 rounded-md text-center mt-2 max-w-xs">
                    <div className="text-sm font-medium text-blue-800">Tyrosine Hydroxylase (TH)</div>
                    <div className="text-xs text-blue-600">Requires BH4 Cofactor</div>
                  </div>
                  <div className="h-16 w-0.5 bg-blue-400 mx-auto"></div>
                  <div className="p-2 bg-blue-100 rounded-md text-center max-w-xs">
                    <div className="text-sm font-medium text-blue-800">L-DOPA</div>
                  </div>
                  <div className="h-8 w-0.5 bg-blue-400 mx-auto"></div>
                  <div className="p-2 bg-blue-100 rounded-md text-center max-w-xs">
                    <div className="text-sm font-medium text-blue-800">Dopamine / Norepinephrine</div>
                    <div className="text-xs text-blue-600">Mood, Energy, Motivation, Cognition</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute right-12 top-1/3">
                <div className="p-2 bg-red-100 rounded-md text-center mb-2">
                  <div className="text-xs font-medium text-red-800">Inflammation</div>
                  <div className="text-xs text-red-600">(Cytokines, ROS)</div>
                </div>
                <div className="h-16 w-0.5 bg-red-400 mx-auto"></div>
                <div className="p-2 bg-red-100 rounded-md text-center">
                  <div className="text-xs font-medium text-red-800">BH4 Depletion</div>
                </div>
                <svg className="w-16 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          This mechanism contributes to anhedonia, fatigue, low motivation, psychomotor slowing, and cognitive deficits seen in chronic inflammation and aging. It may explain poor response to SSRIs in high-inflammation states. BH4 supplementation and anti-inflammatory approaches may help restore dopamine metabolism.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 16: Inflammaging Summary
  {
    title: "Inflammaging: The Convergence of Aging Hallmarks",
    content: (
      <>
        <p className="mb-4">
          Inflammaging represents the chronic, low-grade, systemic inflammation that develops with age and drives multiple age-related pathologies.
        </p>
        
        <div className="relative bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex justify-center mb-10">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center shadow-md">
              <div className="text-center">
                <Flame className="h-10 w-10 text-red-500 mx-auto" />
                <span className="text-sm font-semibold text-red-800">Inflammaging</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                <BrainCircuit className="h-8 w-8 text-amber-600" />
              </div>
              <div className="text-center">
                <span className="text-xs font-medium text-gray-800">Cellular Senescence</span>
                <span className="block text-xs text-gray-600">(SASP)</span>
              </div>
              <div className="h-10 w-0.5 bg-red-300 mt-1"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <Zap className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-center">
                <span className="text-xs font-medium text-gray-800">Mitochondrial Dysfunction</span>
                <span className="block text-xs text-gray-600">(ROS, DAMPs)</span>
              </div>
              <div className="h-10 w-0.5 bg-red-300 mt-1"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
              <div className="text-center">
                <span className="text-xs font-medium text-gray-800">Gut Dysbiosis</span>
                <span className="block text-xs text-gray-600">(LPS translocation)</span>
              </div>
              <div className="h-10 w-0.5 bg-red-300 mt-1"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                <Target className="h-8 w-8 text-purple-600" />
              </div>
              <div className="text-center">
                <span className="text-xs font-medium text-gray-800">Chronic Infections</span>
                <span className="block text-xs text-gray-600">(e.g., CMV)</span>
              </div>
              <div className="h-10 w-0.5 bg-red-300 mt-1"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-2">
                <Dna className="h-8 w-8 text-red-600" />
              </div>
              <div className="text-center">
                <span className="text-xs font-medium text-gray-800">Genetic Factors</span>
              </div>
              <div className="h-10 w-0.5 bg-red-300 mt-1"></div>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mb-2">
                <div className="rounded-full bg-yellow-500 h-8 w-8"></div>
              </div>
              <div className="text-center">
                <span className="text-xs font-medium text-gray-800">Obesity</span>
                <span className="block text-xs text-gray-600">(Adipokines)</span>
              </div>
              <div className="h-10 w-0.5 bg-red-300 mt-1"></div>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <span className="text-sm font-semibold text-red-800">Consequences:</span>
            <p className="text-sm text-gray-700">Drives most age-related chronic diseases (CVD, neurodegeneration, T2D, cancer, frailty), reduces healthspan & lifespan</p>
          </div>
        </div>
        
        <EvidenceBox title="Research Insight">
          Inflammaging represents a convergence point for multiple hallmarks of aging. Inflammaging itself acts as a driver of further aging processes, creating a self-perpetuating cycle. Inflammation reduction is a promising strategy for extending healthspan and potentially lifespan.
        </EvidenceBox>
      </>
    ),
  },
  
  // Slide 17: Cross-Talk - Overview
  {
    title: "The Web of Influence: Inflammation's Cross-Talk",
    content: (
      <>
        <p className="mb-4">
          Inflammation doesn't exist in isolation—it interacts deeply with other critical physiological systems, creating a complex web of influence.
        </p>
        
        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-28 h-28 rounded-full bg-red-100 flex items-center justify-center border-2 border-red-300">
                <div className="text-center">
                  <Flame className="h-8 w-8 text-red-500 mx-auto" />
                  <span className="text-sm font-semibold text-red-800">Inflammation</span>
                </div>
              </div>
              
              <div className="absolute top-[-80px] left-[-140px]">
                <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center">
                  <div className="text-center">
                    <Activity className="h-6 w-6 text-blue-500 mx-auto" />
                    <span className="text-xs font-semibold text-blue-800">Renin-Angiotensin System</span>
                  </div>
                </div>
                <div className="h-10 w-0.5 bg-blue-300 absolute top-24 left-12 transform rotate-45"></div>
              </div>
              
              <div className="absolute top-[-100px] left-[20px]">
                <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="text-center">
                    <Zap className="h-6 w-6 text-green-500 mx-auto" />
                    <span className="text-xs font-semibold text-green-800">Nervous System</span>
                  </div>
                </div>
                <div className="h-16 w-0.5 bg-green-300 absolute top-24 left-12"></div>
              </div>
              
              <div className="absolute top-[-80px] left-[180px]">
                <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center">
                  <div className="text-center">
                    <Target className="h-6 w-6 text-purple-500 mx-auto" />
                    <span className="text-xs font-semibold text-purple-800">Metabolic Pathways</span>
                  </div>
                </div>
                <div className="h-10 w-0.5 bg-purple-300 absolute top-24 left-12 transform -rotate-45"></div>
              </div>
              
              <div className="absolute top-[100px] left-[-140px]">
                <div className="w-24 h-24 rounded-full bg-amber-100 flex items-center justify-center">
                  <div className="text-center">
                    <Shield className="h-6 w-6 text-amber-500 mx-auto" />
                    <span className="text-xs font-semibold text-amber-800">Innate Immune Sensing</span>
                  </div>
                </div>
                <div className="h-10 w-0.5 bg-amber-300 absolute top-[-24px] left-12 transform -rotate-45"></div>
              </div>
              
              <div className="absolute top-[100px] left-[20px]">
                <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center">
                  <div className="text-center">
                    <RefreshCw className="h-6 w-6 text-red-500 mx-auto" />
                    <span className="text-xs font-semibold text-red-800">Oxidative Stress</span>
                  </div>
                </div>
                <div className="h-16 w-0.5 bg-red-300 absolute top-[-16px] left-12"></div>
              </div>
              
              <div className="absolute top-[100px] left-[180px]">
                <div className="w-24 h-24 rounded-full bg-teal-100 flex items-center justify-center">
                  <div className="text-center">
                    <Beaker className="h-6 w-6 text-teal-500 mx-auto" />
                    <span className="text-xs font-semibold text-teal-800">Gut-Brain Axis</span>
                  </div>
                </div>
                <div className="h-10 w-0.5 bg-teal-300 absolute top-[-24px] left-12 transform rotate-45"></div>
              </div>
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-700 mb-4">
          Understanding these interactions reveals the systemic impact of inflammation and identifies novel therapeutic targets. Cross-talk mechanisms explain how localized inflammation can have widespread effects throughout the body.
        </p>
        
        <EvidenceBox title="Clinical Relevance">
          The cross-talk between inflammation and other physiological systems explains why treating inflammation can improve outcomes across multiple conditions, from metabolic and cardiovascular to neurological and immune disorders.
        </EvidenceBox>
      </>
    ),
  },
  
  // Slide 18: Cross-Talk - Renin-Angiotensin System
  {
    title: "Cross-Talk: RAS & Inflammation",
    content: (
      <>
        <p className="mb-4">
          The Renin-Angiotensin System (RAS) extends far beyond blood pressure regulation, playing a crucial role in inflammatory processes throughout the body.
        </p>
        
        <div className="bg-blue-50 rounded-lg p-5 mb-6">
          <h3 className="font-semibold text-blue-800 mb-3">Beyond Blood Pressure</h3>
          <ul className="list-disc list-inside text-sm text-blue-700 space-y-2 ml-1">
            <li>Local RAS exists in tissues including vasculature, gut, brain, and immune cells</li>
            <li>Angiotensin II (Ang II) via AT1 Receptor: Pro-inflammatory, pro-fibrotic</li>
            <li>Stimulates immune cells, cytokine/adhesion molecule production, oxidative stress</li>
            <li>Activates NF-κB pathway in various cell types</li>
          </ul>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2">Gut Inflammation</h3>
            <p className="text-sm text-green-700">
              Local RAS (Ang II, AT1R) is upregulated in inflammatory bowel disease, contributing to mucosal inflammation, barrier dysfunction, and fibrosis. RAS activation promotes pro-inflammatory macrophage polarization in gut tissue.
            </p>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2">Vascular Inflammation</h3>
            <p className="text-sm text-red-700">
              Ang II promotes endothelial dysfunction, vascular smooth muscle proliferation, oxidative stress, and cytokine release (via NF-κB), driving atherosclerosis and vascular aging. RAS hyperactivation is linked to arterial stiffness.
            </p>
          </div>
        </div>
        
        <div className="relative bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex flex-col items-center">
            <div className="p-2 bg-blue-100 rounded-md text-center mb-4 w-48">
              <div className="text-sm font-semibold text-blue-800">Renin-Angiotensin System</div>
            </div>
            
            <div className="h-8 w-0.5 bg-blue-400"></div>
            
            <div className="p-2 bg-amber-100 rounded-md text-center mb-4 w-48">
              <div className="text-sm font-semibold text-amber-800">Angiotensin II</div>
            </div>
            
            <div className="h-8 w-0.5 bg-red-400"></div>
            
            <div className="p-2 bg-red-100 rounded-md text-center mb-4 w-48">
              <div className="text-sm font-semibold text-red-800">AT1 Receptor</div>
            </div>
            
            <div className="h-8 w-0.5 bg-red-400"></div>
            
            <div className="p-2 bg-red-100 rounded-md text-center w-48">
              <div className="text-sm font-semibold text-red-800">Inflammation</div>
              <div className="text-xs text-red-600">Cytokines, ROS, NF-κB</div>
            </div>
            
            <div className="absolute right-12 top-[40%] bg-green-100 rounded-lg p-2 border border-green-300">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-green-600 mr-2" />
                <div>
                  <div className="text-xs font-semibold text-green-800">Therapeutic Angle</div>
                  <div className="text-xs text-green-700">ARBs (e.g., Telmisartan) block AT1R</div>
                  <div className="text-xs text-green-700">Reduces inflammation markers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          Angiotensin Receptor Blockers (ARBs) like Telmisartan exert direct anti-inflammatory effects in gut and vasculature by blocking AT1R. They reduce inflammatory markers (CRP, ICAM-1, IL-6), ROS, and NF-κB activation. Consider ARBs as potential anti-inflammatory agents, especially in patients with hypertension and inflammatory conditions.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 19: Cross-Talk - Cholinergic Anti-inflammatory Pathway
  {
    title: "Cross-Talk: Vagus Nerve & The Inflammatory Reflex (CAP)",
    content: (
      <>
        <p className="mb-4">
          The Cholinergic Anti-inflammatory Pathway (CAP) represents a direct neural control mechanism for inflammation via the vagus nerve.
        </p>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="relative">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4">
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                  <BrainCircuit className="h-10 w-10 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-purple-800">Brain (Brainstem)</span>
              </div>
              
              <div className="hidden md:block h-0.5 w-16 bg-green-400"></div>
              <div className="block md:hidden h-16 w-0.5 bg-green-400"></div>
              
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-2">
                  <Target className="h-10 w-10 text-green-600" />
                </div>
                <span className="text-sm font-medium text-green-800">Vagus Nerve</span>
              </div>
              
              <div className="hidden md:block h-0.5 w-16 bg-blue-400"></div>
              <div className="block md:hidden h-16 w-0.5 bg-blue-400"></div>
              
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                  <Beaker className="h-10 w-10 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-blue-800">Spleen/Tissues</span>
              </div>
              
              <div className="hidden md:block h-0.5 w-16 bg-amber-400"></div>
              <div className="block md:hidden h-16 w-0.5 bg-amber-400"></div>
              
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                  <Shield className="h-10 w-10 text-amber-600" />
                </div>
                <span className="text-sm font-medium text-amber-800">Macrophages</span>
              </div>
            </div>
            
            <div className="absolute top-[-24px] left-[10%] md:left-[15%] bg-purple-50 rounded p-2 shadow-sm">
              <span className="text-xs text-purple-700">1. Senses cytokines</span>
            </div>
            
            <div className="absolute top-[-24px] left-[35%] md:left-[38%] bg-green-50 rounded p-2 shadow-sm">
              <span className="text-xs text-green-700">2. Efferent signal</span>
            </div>
            
            <div className="absolute top-[-24px] left-[60%] md:left-[62%] bg-blue-50 rounded p-2 shadow-sm">
              <span className="text-xs text-blue-700">3. ACh release</span>
            </div>
            
            <div className="absolute top-[-24px] left-[85%] md:left-[85%] bg-amber-50 rounded p-2 shadow-sm">
              <span className="text-xs text-amber-700">4. α7nAChR binding</span>
            </div>
            
            <div className="absolute bottom-[-40px] right-[15%] md:right-[15%] bg-red-50 rounded p-2 shadow-sm">
              <span className="text-xs text-red-700">5. NF-κB inhibition<br />JAK2/STAT3 activation</span>
            </div>
            
            <div className="absolute bottom-[-40px] left-[15%] md:left-[15%] bg-green-50 rounded p-2 shadow-sm">
              <span className="text-xs text-green-700">6. Reduced cytokine<br />production</span>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-5 rounded-lg mb-6">
          <h3 className="font-semibold text-gray-800 mb-3">Mechanism</h3>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-1">
            <li>Afferent vagus senses peripheral inflammation (cytokines) → Brainstem</li>
            <li>Efferent vagus activated → Releases Acetylcholine (ACh) in spleen/other organs</li>
            <li>ACh binds α7 nicotinic ACh Receptor (α7nAChR) on macrophages</li>
            <li>α7nAChR activation inhibits NF-κB, activates JAK2/STAT3 → Suppresses TNF-α, IL-1β, IL-6 release</li>
            <li>Creates a neural reflex circuit controlling immune response magnitude</li>
          </ul>
        </div>
        
        <ClinicalNote>
          CAP dysfunction is linked to chronic inflammatory and autoimmune diseases. While direct vagus nerve stimulation devices are developing clinically, lifestyle factors like deep breathing, meditation, cold exposure, and singing may enhance vagal tone and modulate inflammation through this pathway.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 20: Cross-Talk - CAP Modulation
  {
    title: "Modulating the CAP: Therapeutic Potential",
    content: (
      <>
        <p className="mb-4">
          The Cholinergic Anti-inflammatory Pathway (CAP) offers novel therapeutic approaches for inflammatory conditions without broad immunosuppression.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-3 flex items-center">
              <Zap className="h-5 w-5 mr-2 text-blue-600" />
              Vagus Nerve Stimulation (VNS)
            </h3>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-2 ml-1">
              <li>Electrical stimulation activates CAP:
                <ul className="list-circle list-inside ml-4 mt-1">
                  <li>Implanted devices (surgical)</li>
                  <li>Transcutaneous (taVNS) - ear or neck</li>
                </ul>
              </li>
              <li>Clinical applications:
                <ul className="list-circle list-inside ml-4 mt-1">
                  <li>Shows promise in rheumatoid arthritis, IBD</li>
                  <li>Investigated for chronic pain, post-Lyme/mold syndromes</li>
                </ul>
              </li>
              <li>taVNS advantages: Minimally invasive, well-tolerated, adaptable dose</li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-5 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-3 flex items-center">
              <Pill className="h-5 w-5 mr-2 text-green-600" />
              Pharmacological Modulation
            </h3>
            <ul className="list-disc list-inside text-sm text-green-700 space-y-2 ml-1">
              <li>Low-Dose Nicotine:
                <ul className="list-circle list-inside ml-4 mt-1">
                  <li>α7nAChR agonist, mimics ACh, suppresses cytokines</li>
                  <li>Potential use in chronic pain/post-viral states</li>
                </ul>
              </li>
              <li>Melanocortins (α-MSH, Melanotan II, PT-141):
                <ul className="list-circle list-inside ml-4 mt-1">
                  <li>Potent anti-inflammatory effects</li>
                  <li>May act via central activation of CAP</li>
                  <li>Relevant for neuroinflammation</li>
                </ul>
              </li>
              <li>GTS-21: Selective α7nAChR agonist under investigation</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 flex items-center justify-center">
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <div className="flex flex-col items-center p-3">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-8 w-8 text-blue-600">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 12h8M12 8v8" />
                </svg>
              </div>
              <span className="text-sm font-medium text-blue-800">VNS Device</span>
            </div>
            
            <div className="flex flex-col items-center p-3">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-8 w-8 text-amber-600">
                  <rect x="8" y="2" width="8" height="20" rx="2" />
                </svg>
              </div>
              <span className="text-sm font-medium text-amber-800">Nicotine Patch</span>
            </div>
            
            <div className="flex flex-col items-center p-3">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-8 w-8 text-green-600">
                  <path d="M12 22s8-5 8-12a8 8 0 0 0-16 0c0 7 8 12 8 12z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-green-800">Peptide Injection</span>
            </div>
            
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-8 w-8 text-gray-400">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
            
            <div className="flex flex-col items-center p-3">
              <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                <Target className="h-8 w-8 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-purple-800">CAP Activation</span>
            </div>
            
            <div className="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-8 w-8 text-gray-400">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </div>
            
            <div className="flex flex-col items-center p-3">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-2">
                <Flame className="h-8 w-8 text-red-600" />
              </div>
              <span className="text-sm font-medium text-red-800">Reduced Inflammation</span>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          CAP-based therapies offer a non-immunosuppressive approach to chronic inflammation. They're particularly promising for conditions with poor response to conventional anti-inflammatory approaches or where traditional therapies carry significant side effects. Consider as complementary approaches when developing comprehensive anti-inflammatory protocols.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 21: Cross-Talk - Metabolic Health
  {
    title: "Cross-Talk: Metabolism & Inflammation - A Two-Way Street",
    content: (
      <>
        <p className="mb-4">
          The relationship between metabolism and inflammation is bidirectional, creating a complex feedback system that can lead to chronic disease when dysregulated.
        </p>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              <div className="bg-red-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-red-800 mb-2 flex items-center">
                  <ArrowRight className="h-5 w-5 mr-2 text-red-600" />
                  Inflammation → Metabolic Dysfunction
                </h3>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-1 ml-1">
                  <li>Cytokines (TNF-α, IL-6) from inflamed adipose tissue impair insulin signaling</li>
                  <li>Multiple mechanisms:
                    <ul className="list-circle list-inside ml-4 mt-1">
                      <li>IRS-1 inhibition via JNK/IKK phosphorylation</li>
                      <li>SOCS proteins blocking insulin receptor</li>
                      <li>Increased lipolysis and hepatic glucose production</li>
                    </ul>
                  </li>
                  <li>Result: Insulin Resistance → Metabolic Syndrome → T2D</li>
                </ul>
              </div>
            </div>
            
            <div className="flex-1">
              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                  <ArrowLeft className="h-5 w-5 mr-2 text-blue-600" />
                  Metabolic Dysfunction → Inflammation
                </h3>
                <ul className="list-disc list-inside text-sm text-blue-700 space-y-1 ml-1">
                  <li>Obesity/Adipocyte Stress → Adipokine/Chemokine release → Adipose Tissue Macrophage activation</li>
                  <li>Nutrient Excess:
                    <ul className="list-circle list-inside ml-4 mt-1">
                      <li>FFAs activate TLR4 pathway</li>
                      <li>Hyperglycemia drives NF-κB activation</li>
                      <li>Cellular metabolites activate NLRP3 inflammasome</li>
                    </ul>
                  </li>
                  <li>Gut Dysbiosis → Leaky Gut → Metabolic Endotoxemia (LPS) → Systemic inflammation</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="flex justify-center items-center my-4">
            <div className="w-28 h-28 rounded-full bg-red-100 flex items-center justify-center">
              <div className="text-center">
                <Flame className="h-10 w-10 text-red-500 mx-auto" />
                <span className="text-sm font-semibold text-red-800">Inflammation</span>
              </div>
            </div>
            
            <div className="w-16 relative">
              <div className="h-0.5 w-full bg-purple-400"></div>
              <div className="absolute -top-3 left-0 w-full">
                <div className="flex justify-between">
                  <ArrowRight className="h-5 w-5 text-purple-600" />
                  <ArrowLeft className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </div>
            
            <div className="w-28 h-28 rounded-full bg-blue-100 flex items-center justify-center">
              <div className="text-center">
                <Activity className="h-10 w-10 text-blue-500 mx-auto" />
                <span className="text-sm font-semibold text-blue-800">Metabolic Health</span>
              </div>
            </div>
          </div>
        </div>
        
        <EvidenceBox title="Breaking the Cycle">
          Interventions targeting both inflammation and metabolism simultaneously may be more effective than addressing either in isolation. Time-restricted eating, exercise, and Mediterranean diet patterns demonstrate benefits by affecting both axes. Metformin and GLP-1 agonists have shown dual anti-inflammatory and metabolic benefits, possibly explaining their effectiveness beyond glucose control.
        </EvidenceBox>
      </>
    ),
  },
  
  // Slide 22: Cross-Talk - Nervous System 
  {
    title: "Cross-Talk: Inflammaging & The Nervous System",
    content: (
      <>
        <p className="mb-4">
          The reciprocal relationship between inflammation and the nervous system critically influences both neural function and immune modulation.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <h3 className="font-semibold text-red-800 mb-2 flex items-center">
              <BrainCircuit className="h-5 w-5 mr-2 text-red-600" />
              Neuroinflammation
            </h3>
            <p className="text-sm text-red-700 mb-2">
              Inflammation impacts brain function via multiple mechanisms:
            </p>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1 ml-1">
              <li>Cytokines cross blood-brain barrier at leaky regions or via active transport</li>
              <li>Vagal afferent neurons detect peripheral cytokines and signal to brain</li>
              <li>Microglial activation spreads inflammatory signaling</li>
              <li>Neuroinflammation disrupts:
                <ul className="list-circle list-inside ml-4 mt-1">
                  <li>Neurotransmitter synthesis and signaling</li>
                  <li>Neuroplasticity and connectivity</li>
                  <li>Blood-brain barrier integrity</li>
                  <li>Glymphatic clearance</li>
                </ul>
              </li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
              <Zap className="h-5 w-5 mr-2 text-purple-600" />
              Aging Nervous System
            </h3>
            <p className="text-sm text-purple-700 mb-2">
              Age-related neural changes promote inflammation:
            </p>
            <ul className="list-disc list-inside text-sm text-purple-700 space-y-1 ml-1">
              <li>Reduced vagal tone and CAP function → Impaired anti-inflammatory control</li>
              <li>Microglial senescence → Exaggerated inflammatory responses</li>
              <li>Increased peroxynitrite → Oxidative damage to neural tissue</li>
              <li>Reduced sleep quality → Poor glymphatic clearance</li>
              <li>Decreased blood-brain barrier integrity → Increased peripheral immune cell infiltration</li>
              <li>Accumulated protein aggregates (Aβ, tau) → NLRP3 inflammasome activation</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <div className="relative">
            <div className="flex justify-center">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center">
                    <div className="text-center">
                      <Flame className="h-8 w-8 text-red-500 mx-auto" />
                      <span className="text-xs font-semibold text-red-800">Inflammaging</span>
                    </div>
                  </div>
                </div>
                
                <div className="h-0.5 w-16 bg-purple-400 hidden md:block"></div>
                <div className="h-16 w-0.5 bg-purple-400 block md:hidden"></div>
                
                <div className="flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full bg-purple-100 flex items-center justify-center">
                    <div className="text-center">
                      <BrainCircuit className="h-8 w-8 text-purple-500 mx-auto" />
                      <span className="text-xs font-semibold text-purple-800">Nervous System</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-md">
                <h4 className="text-sm font-semibold text-blue-800 mb-1">Cognitive Consequences</h4>
                <ul className="list-disc list-inside text-xs text-blue-700 space-y-1 ml-1">
                  <li>Memory and executive function decline</li>
                  <li>Increased neurodegeneration risk</li>
                  <li>Neuropsychiatric symptoms (depression, anxiety)</li>
                  <li>Reduced stress resilience</li>
                </ul>
              </div>
              
              <div className="bg-green-50 p-3 rounded-md">
                <h4 className="text-sm font-semibold text-green-800 mb-1">Therapeutic Implications</h4>
                <ul className="list-disc list-inside text-xs text-green-700 space-y-1 ml-1">
                  <li>Anti-inflammatory approaches for cognitive protection</li>
                  <li>Vagal tone enhancement (meditation, exercise)</li>
                  <li>Sleep optimization for glymphatic clearance</li>
                  <li>BBB support (omega-3s, polyphenols)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          Cognitive symptoms in patients with inflammatory conditions may represent direct neuroinflammatory effects rather than secondary reactions. Addressing systemic inflammation often improves cognitive, mood, and energy complaints. Consider cognitive assessment in patients with chronic inflammatory conditions and anti-inflammatory approaches for patients with age-related cognitive changes.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 23: Cross-Talk - Microbiome
  {
    title: "Cross-Talk: The Microbiome & Inflammaging",
    content: (
      <>
        <p className="mb-4">
          The gut microbiome plays a pivotal role in shaping immune responses and inflammaging through diverse metabolic and immunological pathways.
        </p>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="relative">
            <div className="flex justify-center mb-8">
              <div className="w-28 h-28 rounded-full bg-gradient-to-r from-green-100 to-blue-100 flex items-center justify-center border-2 border-teal-300">
                <div className="text-center">
                  <Beaker className="h-10 w-10 text-teal-500 mx-auto" />
                  <span className="text-sm font-semibold text-teal-800">Microbiome</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-2 text-center">Beneficial Effects</h3>
                <ul className="list-disc list-inside text-sm text-green-700 space-y-2 ml-1">
                  <li>SCFAs (butyrate, propionate, acetate)
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>Enhance epithelial barrier function</li>
                      <li>Promote Treg differentiation</li>
                      <li>Inhibit NF-κB signaling</li>
                      <li>Modulate metabolism</li>
                    </ul>
                  </li>
                  <li>Indole derivatives
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>Aryl hydrocarbon receptor activation</li>
                      <li>Support epithelial barrier</li>
                    </ul>
                  </li>
                  <li>Promote IgA production</li>
                </ul>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-semibold text-amber-800 mb-2 text-center">Aging Effects</h3>
                <ul className="list-disc list-inside text-sm text-amber-700 space-y-2 ml-1">
                  <li>Reduced diversity
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>Fewer beneficial species</li>
                      <li>Loss of SCFA producers</li>
                    </ul>
                  </li>
                  <li>Increased pathobionts
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>More Proteobacteria</li>
                      <li>Elevated Firmicutes/Bacteroidetes ratio</li>
                    </ul>
                  </li>
                  <li>Increased intestinal permeability
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>Leads to metabolic endotoxemia</li>
                    </ul>
                  </li>
                </ul>
              </div>
              
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="font-semibold text-red-800 mb-2 text-center">Inflammatory Effects</h3>
                <ul className="list-disc list-inside text-sm text-red-700 space-y-2 ml-1">
                  <li>LPS translocation
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>TLR4 activation</li>
                      <li>Systemic inflammation</li>
                    </ul>
                  </li>
                  <li>Secondary bile acids
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>Can be pro-inflammatory at high levels</li>
                    </ul>
                  </li>
                  <li>TMAO production
                    <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                      <li>Promotes vascular inflammation</li>
                      <li>Increases CVD risk</li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-5 rounded-lg mb-6">
          <h3 className="font-semibold text-blue-800 mb-2">Clinical Applications</h3>
          <ul className="list-disc list-inside text-sm text-blue-700 space-y-2 ml-1">
            <li>Dietary interventions:
              <ul className="list-circle list-inside ml-4 mt-1">
                <li>Prebiotic fibers: Support SCFA producers (Bifidobacteria, Faecalibacterium)</li>
                <li>Polyphenols: Modulate microbiota composition, reduce inflammation</li>
                <li>Mediterranean diet: Associated with healthier microbiome and reduced inflammaging</li>
              </ul>
            </li>
            <li>Targeted probiotics (strain-specific effects):
              <ul className="list-circle list-inside ml-4 mt-1">
                <li>Lactobacillus plantarum 299v: Reduced LPS, improved barrier function</li>
                <li>Lactobacillus rhamnosus GG: Reduces age-related inflammation</li>
                <li>Bifidobacterium longum BB536: Enhances immune function in elderly</li>
              </ul>
            </li>
            <li>Addressing dysbiosis should be a core component of inflammaging management</li>
          </ul>
        </div>
        
        <EvidenceBox title="Microbiome Aging">
          The gut microbiome "ages" or changes substantially over time, with potential causal impacts on host aging. Studies comparing microbiota transfer from young to aged animals demonstrate that young microbiota can reduce inflammaging markers and improve age-related outcomes. Conversely, aged microbiota transfer to young animals accelerates inflammation and aging-like phenotypes.
        </EvidenceBox>
      </>
    ),
  },
  
  // Slide 24: Specialized Pro-resolving Mediators
  {
    title: "Specialized Pro-resolving Mediators (SPMs): Beyond Anti-Inflammation",
    content: (
      <>
        <p className="mb-4">
          SPMs are bioactive lipid mediators that actively orchestrate the resolution phase of inflammation, distinct from traditional anti-inflammatory approaches.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
              <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
              SPM Classification
            </h3>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-2 ml-1">
              <li>Lipoxins (from arachidonic acid)
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Lipoxin A₄, Lipoxin B₄</li>
                </ul>
              </li>
              <li>E-series Resolvins (from EPA)
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Resolvin E1, E2, E3</li>
                </ul>
              </li>
              <li>D-series Resolvins (from DHA)
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Resolvin D1, D2, D3, D4, D5, D6</li>
                </ul>
              </li>
              <li>Protectins (from DHA)
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Protectin D1, Protectin DX</li>
                </ul>
              </li>
              <li>Maresins (from DHA)
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Maresin 1, Maresin 2</li>
                </ul>
              </li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-green-600" />
              Key Actions
            </h3>
            <ul className="list-disc list-inside text-sm text-green-700 space-y-2 ml-1">
              <li>Limit neutrophil recruitment and infiltration</li>
              <li>Promote non-phlogistic macrophage phagocytosis (efferocytosis)</li>
              <li>Enhance macrophage M2 polarization</li>
              <li>Reduce pro-inflammatory cytokine production</li>
              <li>Accelerate tissue regeneration</li>
              <li>Reduce pain via direct actions on nociceptors</li>
              <li>Restore barrier integrity in epithelial/endothelial tissues</li>
              <li>Promote lymphatic drainage of inflammatory exudates</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <div className="relative">
            <h3 className="font-semibold text-gray-800 mb-4 text-center">SPM Synthesis Pathway</h3>
            
            <div className="flex flex-col items-center mb-6">
              <div className="grid grid-cols-3 gap-4 w-full">
                <div className="flex flex-col items-center">
                  <div className="p-2 bg-blue-100 rounded-md text-center max-w-xs">
                    <div className="text-sm font-medium text-blue-800">Arachidonic Acid</div>
                    <div className="text-xs text-blue-600">(Omega-6)</div>
                  </div>
                  <div className="h-8 w-0.5 bg-blue-400"></div>
                  <div className="p-2 bg-green-100 rounded-md text-center max-w-xs">
                    <div className="text-sm font-medium text-green-800">Lipoxins</div>
                  </div>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="p-2 bg-blue-100 rounded-md text-center max-w-xs">
                    <div className="text-sm font-medium text-blue-800">EPA</div>
                    <div className="text-xs text-blue-600">(Omega-3)</div>
                  </div>
                  <div className="h-8 w-0.5 bg-blue-400"></div>
                  <div className="p-2 bg-green-100 rounded-md text-center max-w-xs">
                    <div className="text-sm font-medium text-green-800">E-series Resolvins</div>
                  </div>
                </div>
                
                <div className="flex flex-col items-center">
                  <div className="p-2 bg-blue-100 rounded-md text-center max-w-xs">
                    <div className="text-sm font-medium text-blue-800">DHA</div>
                    <div className="text-xs text-blue-600">(Omega-3)</div>
                  </div>
                  <div className="h-8 w-0.5 bg-blue-400"></div>
                  <div className="grid grid-cols-3 gap-2 w-full">
                    <div className="p-2 bg-green-100 rounded-md text-center">
                      <div className="text-xs font-medium text-green-800">D-Resolvins</div>
                    </div>
                    <div className="p-2 bg-green-100 rounded-md text-center">
                      <div className="text-xs font-medium text-green-800">Protectins</div>
                    </div>
                    <div className="p-2 bg-green-100 rounded-md text-center">
                      <div className="text-xs font-medium text-green-800">Maresins</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-amber-50 p-3 rounded-lg mb-4">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">Enzymes Involved</h4>
              <p className="text-xs text-amber-700">
                Synthesis requires coordinated action of lipoxygenases (5-LOX, 12-LOX, 15-LOX), cyclooxygenases (COX-2), and cytochrome P450 enzymes. Aspirin-modified COX-2 can enhance certain SPM production (aspirin-triggered lipoxins).
              </p>
            </div>
            
            <div className="bg-red-50 p-3 rounded-lg">
              <h4 className="text-sm font-semibold text-red-800 mb-2">Aging & Chronic Disease Impact</h4>
              <p className="text-xs text-red-700">
                SPM production capacity declines with aging and chronic inflammatory diseases, potentially contributing to "resolution deficit" and non-resolving inflammation. This may explain why omega-3 supplementation alone shows variable results in age-related inflammation.
              </p>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          SPM supplementation represents an emerging approach for resolving chronic inflammation without immunosuppression. Limited clinical products exist currently, though specialized fish oil formulations with higher SPM content or precursors are available. For omega-3 supplements, higher doses (2-4g EPA+DHA) may be needed to meaningfully impact resolution pathways in elderly patients with resolution deficits.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 25: Inflammation Biomarkers
  {
    title: "Practical Clinical Biomarkers of Inflammation",
    content: (
      <>
        <p className="mb-4">
          Assessing inflammation in clinical practice requires a strategic approach to biomarker selection and interpretation.
        </p>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4 text-center">Biomarker Spectrum</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">Basic Screening</h4>
              <ul className="list-disc list-inside text-sm text-blue-700 space-y-2 ml-1">
                <li>High-sensitivity CRP (hsCRP)
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>{"<"}1 mg/L: Low risk</li>
                    <li>1-3 mg/L: Moderate risk</li>
                    <li>{">"}3 mg/L: High risk</li>
                  </ul>
                </li>
                <li>CBC with differential
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Neutrophil:Lymphocyte ratio (NLR)</li>
                    <li>Platelet:Lymphocyte ratio (PLR)</li>
                    <li>Monocyte count</li>
                  </ul>
                </li>
                <li>Erythrocyte Sedimentation Rate (ESR)</li>
                <li>Fibrinogen</li>
              </ul>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="text-sm font-semibold text-purple-800 mb-2">Intermediate Panel</h4>
              <ul className="list-disc list-inside text-sm text-purple-700 space-y-2 ml-1">
                <li>Cytokines (serum)
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>IL-6: Strong aging marker</li>
                    <li>TNF-α: Adipose, gut barrier</li>
                    <li>IL-1β: NLRP3 activation</li>
                  </ul>
                </li>
                <li>Glycosylated proteins
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>GlycA (NMR spectroscopy)</li>
                  </ul>
                </li>
                <li>Omega-3 Index (RBC membrane)</li>
                <li>Endotoxin (LPS) or LPS-binding protein</li>
                <li>Ferritin (acute phase reactant)</li>
              </ul>
            </div>
            
            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="text-sm font-semibold text-amber-800 mb-2">Advanced Testing</h4>
              <ul className="list-disc list-inside text-sm text-amber-700 space-y-2 ml-1">
                <li>Oxidative stress markers
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>8-isoprostane (lipid peroxidation)</li>
                    <li>8-OHdG (DNA oxidation)</li>
                    <li>MPO (Myeloperoxidase)</li>
                  </ul>
                </li>
                <li>Specialized pro-resolving mediators (SPMs)</li>
                <li>Inflammasome panels 
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>NLRP3 activity assessment</li>
                  </ul>
                </li>
                <li>Senescence markers (SASP components)</li>
                <li>Cytokine stimulation tests
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Functional immune capacity</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="mt-6 bg-green-50 p-4 rounded-lg border border-green-200">
            <h4 className="text-sm font-semibold text-green-800 mb-2">Practical Approach to Inflammaging Assessment</h4>
            <ol className="list-decimal list-inside text-sm text-green-700 space-y-1 ml-1">
              <li>Begin with quarterly hsCRP and NLR tracking</li>
              <li>Add IL-6 and TNF-α for patients with persistent elevation</li>
              <li>Consider GlycA as a stable inflammatory marker (less daily variability than CRP)</li>
              <li>Omega-3 Index provides insight into resolution capacity</li>
              <li>Ferritin as combined inflammatory/iron status marker (with iron studies)</li>
              <li>Advanced testing reserved for complex cases or research</li>
            </ol>
          </div>
        </div>
        
        <ClinicalNote>
          When interpreting inflammatory biomarkers, always consider potential confounders (recent exercise, minor infections, sleep disruption) and establish a personal baseline through repeated measurements. Track trends over time rather than focusing on isolated values. Combine markers when possible for a more comprehensive picture of the inflammatory state.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 26: Therapeutic Interventions - Pharmaceutical
  {
    title: "Targeting Inflammation: Pharmaceutical Approaches",
    content: (
      <>
        <p className="mb-4">
          Pharmaceutical interventions offer specific targeted pathways to modulate inflammation with varying levels of precision and potential side effects.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
              <Pill className="h-5 w-5 mr-2 text-blue-600" />
              Traditional Anti-inflammatories
            </h3>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-2 ml-1">
              <li>NSAIDs
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>COX-1/2 inhibition (variable selectivity)</li>
                  <li>Reduce PGE2, limit neutrophil recruitment</li>
                  <li>Concerns: GI damage, renal impairment, CV risk</li>
                  <li>Paradoxically may inhibit resolution by blocking PGD2 and lipoxin synthesis</li>
                </ul>
              </li>
              <li>Corticosteroids
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Inhibit multiple inflammatory pathways via NF-κB suppression</li>
                  <li>Broad immunosuppressive effects</li>
                  <li>Concerns: metabolic/bone effects, HPA suppression</li>
                  <li>Low-dose or pulsed regimens may limit side effects</li>
                </ul>
              </li>
              <li>Antimalarials
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Hydroxychloroquine: TLR blockade, lysosomal stabilization</li>
                  <li>Anti-senescent effects</li>
                  <li>Relatively good long-term safety profile</li>
                </ul>
              </li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
              <Target className="h-5 w-5 mr-2 text-purple-600" />
              Targeted Biologic Therapies
            </h3>
            <ul className="list-disc list-inside text-sm text-purple-700 space-y-2 ml-1">
              <li>Cytokine-specific inhibitors
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>TNF-α: adalimumab, infliximab, etanercept</li>
                  <li>IL-1β: anakinra, canakinumab</li>
                  <li>IL-6: tocilizumab</li>
                  <li>IL-17/IL-23: secukinumab, ustekinumab</li>
                </ul>
              </li>
              <li>JAK/STAT inhibitors
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Tofacitinib, baricitinib, upadacitinib</li>
                  <li>Block multiple cytokine signaling pathways</li>
                  <li>Oral administration</li>
                </ul>
              </li>
              <li>Concerns: Infection risk, cost, specific indications</li>
              <li>Rapidly evolving field with increasing precision</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3 text-center">Repurposed Medications with Anti-inflammatory Effects</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 bg-green-50 rounded-md">
              <h4 className="text-sm font-semibold text-green-800 mb-1">Metformin</h4>
              <ul className="list-disc list-inside text-xs text-green-700 space-y-1 ml-1">
                <li>Activates AMPK pathway</li>
                <li>Reduces NF-κB activation</li>
                <li>Improves mitochondrial function</li>
                <li>Modulates gut microbiome</li>
                <li>Clinical dose: 500-2000mg daily</li>
              </ul>
            </div>
            
            <div className="p-3 bg-red-50 rounded-md">
              <h4 className="text-sm font-semibold text-red-800 mb-1">Statins</h4>
              <ul className="list-disc list-inside text-xs text-red-700 space-y-1 ml-1">
                <li>Pleiotropic anti-inflammatory effects</li>
                <li>Reduce CRP independent of lipid effects</li>
                <li>Immunomodulatory via prenylation inhibition</li>
                <li>Enhance endothelial function</li>
                <li>Rosuvastatin, atorvastatin most studied</li>
              </ul>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-md">
              <h4 className="text-sm font-semibold text-amber-800 mb-1">GLP-1 Agonists</h4>
              <ul className="list-disc list-inside text-xs text-amber-700 space-y-1 ml-1">
                <li>Reduce macrophage activation</li>
                <li>Inhibit NLRP3 inflammasome</li>
                <li>Decrease adipose inflammation</li>
                <li>Improve cardio-metabolic parameters</li>
                <li>Semaglutide most potent for weight/inflammation</li>
              </ul>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          When selecting pharmacological approaches to inflammation, consider the specific inflammatory pathways involved in the patient's condition. Repurposed medications may offer better safety profiles for chronic use in aging patients compared to traditional anti-inflammatories. For acute interventions, target specific cytokines; for chronic management, consider combined approaches that address both symptoms and underlying mechanisms.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 27: Therapeutic Interventions - Lifestyle & Nutrition 
  {
    title: "Inflammation Modulation: Lifestyle & Nutrition Strategies",
    content: (
      <>
        <p className="mb-4">
          Lifestyle and nutritional interventions form the foundation of sustainable inflammation management, often with fewer side effects than pharmacological approaches.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              Physical Activity
            </h3>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1 ml-1">
              <li>Anti-inflammatory mechanisms:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Reduces visceral adiposity</li>
                  <li>Increases anti-inflammatory myokines (IL-6, IL-10, IL-15)</li>
                  <li>Enhances vagal tone (CAP activation)</li>
                  <li>Improves mitochondrial biogenesis and quality control</li>
                  <li>Reduces cellular senescence burden</li>
                </ul>
              </li>
              <li>Practical recommendations:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Combined resistance + aerobic training most effective</li>
                  <li>150-300 min/week moderate or 75-150 min/week vigorous</li>
                  <li>Resistance training ≥2 days/week</li>
                  <li>Avoid excessive high-intensity training in inflammatory states</li>
                  <li>Post-exercise recovery critical (nutrition, sleep, stress management)</li>
                </ul>
              </li>
            </ul>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <h3 className="font-semibold text-green-800 mb-2 flex items-center">
              <Beaker className="h-5 w-5 mr-2 text-green-600" />
              Nutrition & Bioactive Compounds
            </h3>
            <ul className="list-disc list-inside text-sm text-green-700 space-y-1 ml-1">
              <li>Dietary patterns:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Mediterranean: Strong evidence for reducing inflammatory markers</li>
                  <li>DASH: Beneficial for vascular inflammation</li>
                  <li>Plant-forward: Higher polyphenol intake, lower AGE content</li>
                </ul>
              </li>
              <li>Key nutrients & bioactives:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Omega-3s (EPA/DHA): 2-4g/day therapeutic dose</li>
                  <li>Polyphenols (curcumin, EGCG, resveratrol, quercetin)</li>
                  <li>Prebiotic fibers: Support SCFA production</li>
                  <li>Tocopherols, carotenoids: Antioxidant support</li>
                </ul>
              </li>
              <li>Anti-inflammatory eating practices:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Time-restricted eating (12-16hr overnight fast)</li>
                  <li>Low-AGE cooking methods (lower temps, moisture, acidity)</li>
                  <li>Minimizing ultra-processed foods</li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3 text-center">Additional Lifestyle Anti-inflammatory Approaches</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 bg-purple-50 rounded-md">
              <h4 className="text-sm font-semibold text-purple-800 mb-1">Stress Management</h4>
              <ul className="list-disc list-inside text-xs text-purple-700 space-y-1 ml-1">
                <li>Chronic stress promotes inflammation:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>HPA axis disruption → cortisol dysregulation</li>
                    <li>Sympathetic dominance → CAP suppression</li>
                    <li>Sleep disruption → glymphatic impairment</li>
                  </ul>
                </li>
                <li>Evidence-based interventions:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Mindfulness meditation: ↓NF-κB activity</li>
                    <li>Yoga: ↓IL-6, ↓TNF-α, ↑vagal activity</li>
                    <li>Breathing practices: ↑CAP activation</li>
                  </ul>
                </li>
              </ul>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-md">
              <h4 className="text-sm font-semibold text-blue-800 mb-1">Sleep Optimization</h4>
              <ul className="list-disc list-inside text-xs text-blue-700 space-y-1 ml-1">
                <li>Sleep disruption mechanisms:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>↑ NF-κB transcription</li>
                    <li>↑ Inflammasome activation</li>
                    <li>↓ Glymphatic clearance</li>
                  </ul>
                </li>
                <li>Interventions:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Sleep duration: 7-9 hours adults</li>
                    <li>Sleep timing: Align with circadian rhythm</li>
                    <li>Sleep quality: Address disorders (OSA)</li>
                    <li>Optimize sleep environment (temperature, light)</li>
                  </ul>
                </li>
              </ul>
            </div>
            
            <div className="p-3 bg-red-50 rounded-md">
              <h4 className="text-sm font-semibold text-red-800 mb-1">Environmental Factors</h4>
              <ul className="list-disc list-inside text-xs text-red-700 space-y-1 ml-1">
                <li>Minimize exposures:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Air pollution (PM2.5): ↑ systemic inflammation</li>
                    <li>Endocrine disruptors: ↑ immunometabolic disruption</li>
                    <li>Heavy metals: ↑ oxidative stress</li>
                  </ul>
                </li>
                <li>Protection strategies:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>HEPA filtration</li>
                    <li>Detoxification support (glutathione optimization)</li>
                    <li>Microbiome support for biotransformation</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <EvidenceBox title="Synergistic Approach">
          Lifestyle interventions for inflammation work best when implemented together rather than in isolation. Research shows synergistic effects between exercise, diet, sleep, and stress management. For example, the anti-inflammatory effects of Mediterranean diet are amplified when combined with regular physical activity and stress reduction techniques.
        </EvidenceBox>
      </>
    ),
  },
  
  // Slide 28: Therapeutic Interventions - Novel Approaches
  {
    title: "Emerging & Innovative Anti-inflammatory Approaches",
    content: (
      <>
        <p className="mb-4">
          The field of inflammation modulation is rapidly evolving with novel therapeutic approaches targeting specific pathways involved in inflammaging.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
              <Target className="h-5 w-5 mr-2 text-blue-600" />
              Senescence-Targeting Approaches
            </h3>
            <p className="text-sm text-blue-700 mb-2">
              Targeting the senescent cell burden to reduce SASP-mediated inflammation:
            </p>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1 ml-1">
              <li>Senolytics:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Dasatinib + Quercetin combination</li>
                  <li>Fisetin: Natural flavonoid with senolytic activity</li>
                  <li>Navitoclax and related BCL inhibitors</li>
                  <li>Emerging small molecules with tissue specificity</li>
                </ul>
              </li>
              <li>Senomorphics:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>Rapamycin/rapalogs: mTOR inhibition</li>
                  <li>Metformin: Multiple anti-senescence mechanisms</li>
                  <li>JAK inhibitors: Suppress SASP</li>
                </ul>
              </li>
              <li>Challenges: Cell type specificity, dosing schedule optimization, biomarkers of efficacy</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
              <Zap className="h-5 w-5 mr-2 text-purple-600" />
              Inflammasome Modulation
            </h3>
            <p className="text-sm text-purple-700 mb-2">
              Targeting the NLRP3 inflammasome and other inflammatory hubs:
            </p>
            <ul className="list-disc list-inside text-sm text-purple-700 space-y-1 ml-1">
              <li>Direct NLRP3 inhibitors:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>MCC950/CRID3: Most well-studied preclinical</li>
                  <li>OLT1177 (Dapansutrile): In clinical development</li>
                  <li>Tranilast, Oridonin: Repurposed/natural compounds</li>
                </ul>
              </li>
              <li>Upstream modulators:
                <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                  <li>NRF2 activators (sulforaphane, DMF)</li>
                  <li>Mitochondrial stabilizers</li>
                  <li>Inhibitors of oxidized mitochondrial DNA release</li>
                </ul>
              </li>
              <li>Emerging: Tissue-specific NLRP3 targeting to maintain beneficial inflammatory responses</li>
            </ul>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-5 mb-6">
          <h3 className="font-semibold text-gray-800 mb-3 text-center">Additional Innovative Approaches</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 bg-green-50 rounded-md">
              <h4 className="text-sm font-semibold text-green-800 mb-1">Pro-Resolution Therapy</h4>
              <ul className="list-disc list-inside text-xs text-green-700 space-y-1 ml-1">
                <li>SPM-based interventions:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Synthetic SPMs (Resolvin E1, Maresin 1)</li>
                    <li>SPM precursor supplementation</li>
                    <li>15-LOX/5-LOX pathway enhancers</li>
                  </ul>
                </li>
                <li>Additional approaches:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Annexin A1 mimetics</li>
                    <li>Enhancing efferocytosis</li>
                    <li>SPM-producing probiotics</li>
                  </ul>
                </li>
              </ul>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-md">
              <h4 className="text-sm font-semibold text-amber-800 mb-1">Microbiome Modulation</h4>
              <ul className="list-disc list-inside text-xs text-amber-700 space-y-1 ml-1">
                <li>Next-generation approaches:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Precision strain-specific probiotics</li>
                    <li>Postbiotics (bacterial metabolites)</li>
                    <li>Engineered bacteria producing anti-inflammatories</li>
                    <li>SCFA-producing consortia</li>
                    <li>Fecal microbiota transplantation</li>
                  </ul>
                </li>
                <li>Emerging clinical evidence:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Butyrate supplementation</li>
                    <li>Specific strains for inflammatory diseases</li>
                  </ul>
                </li>
              </ul>
            </div>
            
            <div className="p-3 bg-red-50 rounded-md">
              <h4 className="text-sm font-semibold text-red-800 mb-1">Neuromodulation</h4>
              <ul className="list-disc list-inside text-xs text-red-700 space-y-1 ml-1">
                <li>Cholinergic pathway activation:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Direct vagus nerve stimulation (VNS)</li>
                    <li>Transcutaneous auricular VNS (taVNS)</li>
                    <li>Non-invasive brain stimulation</li>
                    <li>Virtual reality-based autonomic modulation</li>
                  </ul>
                </li>
                <li>Advantages:
                  <ul className="list-circle list-inside ml-4 mt-1">
                    <li>Targeted inflammation control</li>
                    <li>Minimal systemic immunosuppression</li>
                    <li>Potential for personalized dosing</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <ClinicalNote>
          When considering innovative approaches, weigh the evidence carefully and recognize that newer interventions often lack long-term safety data. Consider implementing approaches with the strongest safety profile first (lifestyle interventions, repurposed medications) while keeping abreast of emerging therapies. The field is rapidly developing, with several clinical trials underway that may change practice within 5 years.
        </ClinicalNote>
      </>
    ),
  },
  
  // Slide 29: Conclusion and Case Integration
  {
    title: "Conclusion: Inflammation as a Clinical Integration Node",
    content: (
      <>
        <p className="mb-4">
          Inflammation represents a central integration node that connects multiple physiological systems and aging pathways, making it both a biomarker and therapeutic target.
        </p>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="font-semibold text-gray-800 mb-4 text-center">Key Principles for Clinical Practice</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">Assessment Framework</h4>
              <ol className="list-decimal list-inside text-sm text-blue-700 space-y-2 ml-1">
                <li>Identify inflammation drivers
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Chronic infections/dysbiosis</li>
                    <li>Metabolic dysfunction</li>
                    <li>Environmental exposures</li>
                    <li>Cellular senescence burden</li>
                    <li>Psychosocial stressors</li>
                  </ul>
                </li>
                <li>Quantify inflammatory burden
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Biomarkers (standard + targeted)</li>
                    <li>Clinical manifestations</li>
                    <li>Functional assessments</li>
                  </ul>
                </li>
                <li>Evaluate resolution capacity
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Omega-3 status</li>
                    <li>Vagal tone</li>
                    <li>Acute-to-chronic transition</li>
                  </ul>
                </li>
                <li>Assess system-specific impacts
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Cardiovascular</li>
                    <li>Neurological</li>
                    <li>Metabolic</li>
                    <li>Musculoskeletal</li>
                  </ul>
                </li>
              </ol>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="text-sm font-semibold text-green-800 mb-2">Therapeutic Approach</h4>
              <ol className="list-decimal list-inside text-sm text-green-700 space-y-2 ml-1">
                <li>Layer interventions strategically
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Begin with foundation: Diet, exercise, sleep, stress</li>
                    <li>Add targeted nutrients/bioactives</li>
                    <li>Consider pharmaceutical approaches as needed</li>
                    <li>Integrate innovative approaches when appropriate</li>
                  </ul>
                </li>
                <li>Balance suppression & resolution
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Don't merely suppress; enhance resolution</li>
                    <li>Maintain beneficial inflammatory responses</li>
                    <li>Address upstream causes when possible</li>
                  </ul>
                </li>
                <li>Personalize approach
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Consider genetic factors (e.g., APOE status)</li>
                    <li>Tailor to specific inflammatory phenotypes</li>
                    <li>Adjust based on biomarker response</li>
                  </ul>
                </li>
                <li>Monitor and adjust
                  <ul className="list-circle list-inside ml-4 mt-1 text-xs">
                    <li>Track inflammatory markers longitudinally</li>
                    <li>Assess clinical response</li>
                    <li>Modify approach as needed</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 p-5 rounded-lg mb-6">
          <h3 className="font-semibold text-blue-800 mb-3">Case Integration: Moving From Theory to Practice</h3>
          <p className="text-sm text-blue-700 mb-3">
            Consider a 58-year-old patient with elevated inflammatory markers (hsCRP 3.8 mg/L, IL-6 5.2 pg/mL), mild cognitive complaints, metabolic syndrome, and fatigue. The integrated approach would include:
          </p>
          <ol className="list-decimal list-inside text-sm text-blue-700 space-y-1 ml-1">
            <li>Foundation: Mediterranean diet pattern, 12-16h time-restricted eating, combined aerobic/resistance training, sleep optimization, stress management</li>
            <li>Targeted bioactives: EPA/DHA (3g/day), targeted polyphenols (curcumin, EGCG), prebiotic fiber</li>
            <li>Pharmaceutical considerations: Metformin, potential statin therapy (if indicated), low-dose naltrexone</li>
            <li>Monitoring: Quarterly inflammatory panels, cognitive assessments, metabolic parameters</li>
            <li>Consideration for emerging approaches: SPM precursors, vagal tone enhancement techniques</li>
          </ol>
        </div>
        
        <EvidenceBox title="Final Perspective">
          The field of inflammation in aging represents a critical integration point for multiple biological systems. By addressing chronic inflammation through a systems biology lens, we can potentially modify the trajectory of age-related diseases and extend healthspan. The transition from non-resolving inflammation to effective resolution biology may be one of the most important therapeutic targets for extending healthy longevity.
        </EvidenceBox>
      </>
    ),
  }
];

const InflammationLecture: React.FC = () => {
  const { toast } = useToast();
  
  // Fetch current user data
  const { data: userData } = useQuery<{ email: string }>({
    queryKey: ['/api/user'],
    refetchOnWindowFocus: false,
  });
  
  // Check if the user has special access (only avinjamoori+2@gmail.com)
  // This restricts the download button to only appear for the specific user
  const hasDownloadAccess = userData?.email === 'avinjamoori+2@gmail.com';
  
  // Function to extract plain text from React nodes (simplified)
  const extractTextFromReactNode = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') {
      return node.toString();
    }
    
    if (Array.isArray(node)) {
      return node.map(extractTextFromReactNode).join('\n');
    }
    
    if (React.isValidElement(node)) {
      if (node.props.children) {
        return extractTextFromReactNode(node.props.children);
      }
      return '';
    }
    
    return '';
  };
  
  // Function to handle slide download
  const handleDownloadSlides = () => {
    // Create a rich text representation of the slides
    const slidesContent = lectureSlides.map((slide, index) => {
      const slideNumber = index + 1;
      // Extract text content from the React nodes
      let contentText = '';
      if (React.isValidElement(slide.content)) {
        contentText = extractTextFromReactNode(slide.content);
      }
      
      // Format the slide content
      return `
=================================================================
SLIDE ${slideNumber}: ${slide.title}
=================================================================

${contentText}

-----------------------------------------------------------------
`;
    }).join('\n');
    
    // Add header and footer
    const header = `
INFLAMMATION, INFLAMMAGING, AND LONGEVITY
Lecture Slides | For: ${userData?.email || 'User'}
Date: ${new Date().toLocaleDateString()}
=================================================================

`;
    
    const footer = `
=================================================================
© ${new Date().getFullYear()} Next Generation Medicine
For personal use only. Not for redistribution.
`;
    
    const fullDocument = header + slidesContent + footer;
    
    // Create a Blob with the content
    const blob = new Blob([fullDocument], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a download link and trigger it
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Inflammation_Lecture_Slides.txt';
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    toast({
      title: "Slides downloaded",
      description: "Inflammation lecture slides have been downloaded successfully!",
      variant: "default",
    });
  };
  
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/lectures">Lectures</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>Inflammation</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="flex items-center justify-between mb-6">
          <Link href="/mentorship/lectures">
            <Button variant="outline" size="sm" className="gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back to Lectures
            </Button>
          </Link>
          
          <div className="flex gap-2 items-center">
            <LectureDownloader lectureName="Inflammation" fileName="Inflammation" />
            {hasDownloadAccess && (
              <Button 
                variant="outline" 
                size="sm" 
                className="gap-1 text-green-600 border-green-200 hover:bg-green-50"
                onClick={handleDownloadSlides}
              >
                <Download className="h-4 w-4" />
                Download Slides
              </Button>
            )}
            <Badge variant="outline" className="text-blue-500 border-blue-200 bg-blue-50">
              Cellular Medicine
            </Badge>
          </div>
        </div>
        
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Inflammation, Inflammaging, and Longevity</h1>
        <p className="text-xl text-gray-600 mb-8">
          Mechanisms, Biomarkers, and Clinical Strategies for Health Optimization
        </p>
        
        <Separator className="my-6" />
        
        {/* Video and Slideshow Section */}
        <div className="mb-12">
          <Tabs defaultValue="slideshow" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="slideshow">Slideshow</TabsTrigger>
            </TabsList>
            <TabsContent value="slideshow">
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Audio Narration</h3>
                <div className="w-full flex justify-center">
                  <iframe src="https://share.descript.com/embed/1hQfEMln40Y" width="640" height="360" frameBorder="0" allowFullScreen></iframe>
                </div>
              </div>
              <Slideshow slides={lectureSlides} />
            </TabsContent>
          </Tabs>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <Book className="h-5 w-5 mr-2 text-blue-500" />
                <h3 className="font-semibold text-gray-900">Key Concepts</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Inflammation as an active, two-phase process requiring proper resolution</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Specialized Pro-resolving Mediators (SPMs) as active resolution initiators</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Peroxisomal function in supporting resolution pathways</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Macrophage polarization shift with aging and intervention</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <BrainCircuit className="h-5 w-5 mr-2 text-purple-500" />
                <h3 className="font-semibold text-gray-900">Clinical Applications</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Anti-inflammatory diet patterns with targeted supplements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Resolution-promoting compounds including omega-3 fatty acids</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Lifestyle interventions targeting inflammatory pathways</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Personalized protocols based on inflammatory phenotype</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center mb-4">
                <Heart className="h-5 w-5 mr-2 text-red-500" />
                <h3 className="font-semibold text-gray-900">Health Outcomes</h3>
              </div>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Reduced risk of age-related diseases</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Improved cognitive function and brain health</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Enhanced metabolic health and insulin sensitivity</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 mr-2 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-sm text-gray-700">Potential extension of healthspan through inflammatory modulation</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
        
        <Separator className="my-6" />
        
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Clinical Reference Guide</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-blue-50 p-5 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-900 mb-4">Protocol Framework</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 font-medium">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">Assessments and Biomarkers</p>
                    <p className="text-sm text-gray-700 mt-1">
                      Start with comprehensive inflammatory profiling including hsCRP, IL-6, TNF-α, fibrinogen, and when available, SPM levels or resolution indices. Include oxidative stress markers and metabolic parameters to identify inflammatory phenotype.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 font-medium">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">Foundation Interventions</p>
                    <p className="text-sm text-gray-700 mt-1">
                      All patients benefit from an anti-inflammatory diet pattern (Mediterranean, DASH, or plant-forward), optimized sleep, stress management, and regular physical activity. These foundation interventions should be implemented before more targeted approaches.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-blue-600 font-medium">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">Targeted Protocols</p>
                    <p className="text-sm text-gray-700 mt-1">
                      Tailor specific interventions based on inflammatory phenotype, biomarker patterns, clinical presentation, and patient goals. Consider specific protocols for metabolic inflammation, neuroinflammation, or autoimmune-driven inflammation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-amber-50 p-5 rounded-lg border border-amber-200">
              <h3 className="font-semibold text-amber-900 mb-4">Clinical Pearls</h3>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                    <Lightbulb className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-amber-800">
                    Consider the ratio of EPA to DHA in omega-3 supplementation based on clinical goals: higher EPA for systemic/vascular inflammation, higher DHA for neuroinflammation and resolution pathways.
                  </p>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                    <Lightbulb className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-amber-800">
                    SPM production requires functional enzymes (15-LOX, 5-LOX) which may be inhibited by certain medications or genetic variants. Consider direct SPM supplementation in cases of suspected enzymatic dysfunction.
                  </p>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                    <Lightbulb className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-amber-800">
                    Time omega-3 supplementation strategically: taking with largest meal improves absorption, while splitting doses may enhance incorporation into membranes and SPM production.
                  </p>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 text-amber-600 mr-2 mt-0.5">
                    <Lightbulb className="h-5 w-5" />
                  </div>
                  <p className="text-sm text-amber-800">
                    Consider low-dose aspirin (81mg) for its ability to trigger aspirin-triggered lipoxins and resolvins through acetylated COX-2, especially in patients with cardiometabolic risk and normal bleeding risk.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <Separator className="my-6" />
        
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-10">
          <h2 className="text-xl font-bold mb-4">References and Further Reading</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Key References</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>Serhan CN. Pro-resolving lipid mediators are leads for resolution physiology. Nature. 2014;510(7503):92-101.</li>
                <li>Franceschi C, et al. Inflammaging: a new immune-metabolic viewpoint for age-related diseases. Nat Rev Endocrinol. 2018;14(10):576-590.</li>
                <li>Furman D, et al. Chronic inflammation in the etiology of disease across the life span. Nat Med. 2019;25(12):1822-1832.</li>
                <li>Ramsden CE, et al. Targeted alteration of dietary n-3 and n-6 fatty acids for the treatment of chronic headaches. J Pain. 2013;14(5):438-445.</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">Clinical Guidelines</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li>Calder PC, et al. Health relevance of the modification of low grade inflammation in ageing (inflammageing) and the role of nutrition. Ageing Res Rev. 2017;40:95-119.</li>
                <li>Serhan CN, Levy BD. Resolvins in inflammation: emergence of the pro-resolving superfamily of mediators. J Clin Invest. 2018;128(7):2657-2669.</li>
                <li>Mozaffarian D, Wu JH. Omega-3 fatty acids and cardiovascular disease: effects on risk factors, molecular pathways, and clinical events. J Am Coll Cardiol. 2011;58(20):2047-2067.</li>
                <li>Casas R, et al. The immune-inflammatory response in relation to health: Mediterranean diet as a paradigm. Mol Nutr Food Res. 2021;65(5):e2000130.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default InflammationLecture;